import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { formatError } from '@/lib/errors';
import { localIsoDate } from '@/lib/formatters';
import type { TrackingStatus } from '@/features/procurement/hooks';
import { rollupPrimarySuppliers } from '@/features/inventory/primarySupplier';
import { mainPurchaseUnit, type PurchaseUnit } from '@/lib/uom';

/**
 * Item kinds. Originally a Postgres enum (`public.item_kind`), now a
 * per-organisation lookup table (`public.item_kinds`, migration 42) so
 * super admins can add new kinds without a schema change. Migration 91
 * consolidated the catalog down to the three baseline slugs Atlas now
 * seeds for every org as `is_system = true` rows — in their canonical
 * sidebar / picker order:
 *
 *   * 'raw_stock'     → "Raw material"  (raw, unprocessed stock — raw
 *                                        plastic / steel, lumber, resin)
 *   * 'raw_material'  → "Components"    (part-level items, bought or
 *                                        built; labelled "Raw material"
 *                                        pre-mig 51, slug kept for FK
 *                                        stability)
 *   * 'finished_good' → "Finished good" (SKUs sold to customers)
 *
 * Note the `raw_material` / "Components" label swap from migration 51:
 * the slug stays `raw_material` for downstream FK / config stability,
 * but the user-facing label reads "Components". The `raw_stock` slug
 * carries the "Raw material" label.
 *
 * Legacy slugs `wip` (Sub-assembly), `consumable`, and `shop_supplies`
 * were removed in migration 91 (their items re-filed onto the three
 * survivors), along with any custom kinds.
 *
 * The type stays as a plain string here because new kinds can still be
 * minted at runtime; specific call sites that branch on system slugs
 * (e.g. ItemDetailPage's ITEM_KIND_CONFIG map) narrow with a literal
 * union locally.
 */
export type ItemKind = string;

/**
 * How a multi-select of item types is stored. `items.kind` stays a
 * single required slug; every other checked type goes in
 * `additional_kinds`.
 */
export interface ItemKindSelection {
  kind: ItemKind;
  additional_kinds: ItemKind[];
}

/**
 * Split a checkbox selection into the primary `kind` column and the
 * remaining types. The current primary stays put when it is still
 * checked, so adding another type does not reshuffle the record. When
 * the primary was unchecked, the earliest catalog type among what's
 * left becomes the new primary. At least one type is required.
 */
export function partitionItemKinds(
  selected: readonly string[],
  preferredPrimary: string | null | undefined,
  orderedSlugs: readonly string[],
): ItemKindSelection {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const slug of selected) {
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    unique.push(slug);
  }
  if (unique.length === 0) {
    throw new Error('An item needs at least one type.');
  }
  const order = new Map(orderedSlugs.map((slug, index) => [slug, index]));
  const rank = (slug: string) => order.get(slug) ?? Number.MAX_SAFE_INTEGER;
  const byCatalog = (a: string, b: string) => rank(a) - rank(b) || a.localeCompare(b);
  const kind =
    preferredPrimary && seen.has(preferredPrimary)
      ? preferredPrimary
      : [...unique].sort(byCatalog)[0];
  return {
    kind,
    additional_kinds: unique.filter((slug) => slug !== kind).sort(byCatalog),
  };
}

export const SYSTEM_ITEM_KIND_SLUGS = [
  'raw_stock',
  'raw_material',
  'finished_good',
] as const;
export type SystemItemKindSlug = (typeof SYSTEM_ITEM_KIND_SLUGS)[number];
export type ItemStatus =
  | 'needs_verification'
  | 'concept'
  | 'in_development'
  | 'pre_production'
  | 'active'
  | 'on_hold'
  | 'archived';

/**
 * Closed palette of kind swatches. Matches both the CHECK constraint
 * on `public.item_kinds.color` (migration 41) and `public.item_tags.color`
 * (migration 40) so the badge / swatch class maps in `lib/badge-colors`
 * can render either source.
 */
export const ITEM_KIND_COLORS = [
  'slate',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'indigo',
  'purple',
  'pink',
] as const;

export type ItemKindColor = (typeof ITEM_KIND_COLORS)[number];

export interface ItemKindRow {
  id: string;
  organization_id: string;
  slug: string;
  label: string;
  description: string | null;
  color: ItemKindColor;
  is_system: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface Item {
  id: string;
  organization_id: string;
  sku: string;
  name: string;
  description: string | null;
  kind: ItemKind;
  /**
   * Every type this item belongs to besides `kind`. The catalog Type
   * control is one set of checkboxes; `kind` is whichever checked type
   * we keep in the required column, and the rest live here. Never
   * contains `kind` (migration 106).
   */
  additional_kinds: ItemKind[];
  base_uom: string;
  is_lot_tracked: boolean;
  /** Each unit carries its own serial-number barcode; Work Mode scans it (migration 142). */
  is_serialized: boolean;
  reorder_point: number | null;
  reorder_qty: number | null;
  /**
   * Override for the Overstock status, in the UoM Produced. Null uses
   * reorder_point + reorder_qty (migration 165).
   */
  overstock_at: number | null;
  /**
   * Built on our own machines rather than bought. At or below the reorder
   * point, a draft production order is created for review (migration 154).
   */
  is_made_in_house: boolean;
  /** Machine / workstation that normally makes it; auto-drafts are assigned here. */
  default_work_center_id: string | null;
  /** Web page a purchased item is bought from (migration 160). */
  purchase_url: string | null;
  std_cost_cents: number | null;
  list_price_cents: number | null;
  /** Dealer price per unit at the 4 / 18 / 180 unit MOQ tiers. */
  dealer_price_4_cents: number | null;
  dealer_price_18_cents: number | null;
  dealer_price_180_cents: number | null;
  /** Dealer minimum order quantity for parts / accessories. */
  dealer_moq: number | null;
  /** Family / variant-group SKU this item rolls up to, e.g. FG-RNW-MAN. */
  parent_sku: string | null;
  /** Models or sub-assemblies the item applies to, e.g. ["Manual"]. */
  model_filters: string[];
  /** Market the SKU is packaged for, e.g. "US", "EU", "AU/NZ". */
  region: string | null;
  /** Virro Shopify product page and Amazon listing (migration 163). */
  shopify_url: string | null;
  amazon_url: string | null;
  category: string | null;
  /**
   * Free-form colourway descriptor for the product (e.g. "Black",
   * "Olive"). Distinct from `item_tags` which are colour-coded labels
   * for cross-cutting criteria — see migration 41.
   */
  color: string | null;
  /**
   * Lead time as quoted, verbatim (e.g. "2 days", "unknown, expect
   * 2-4 weeks"). `lead_time_worst_case_days` carries the sortable,
   * single-unit (days) projection of the same figure — see migration 94.
   */
  lead_time_text: string | null;
  lead_time_worst_case_days: number | null;
  /** General free-text comments (migration 94). */
  notes: string | null;
  status: ItemStatus;
  is_active: boolean;
  /**
   * Workstations attached to this item, hydrated by `useItems` via the
   * `item_workstations` join so the grid can show + facet-filter by
   * station. Absent on single-item fetches (`useItem`), which don't
   * join — the detail page reads the set through `useWorkstationsForItem`
   * instead. See migration 94.
   */
  workstations?: Array<{ id: string; name: string }>;
  /**
   * Storage object key for the primary product photo, of the form
   * `<organization_id>/<item_id>/<filename>` inside the `item-images`
   * bucket. Null when no photo has been uploaded yet. The detail page
   * mints a short-lived signed URL on demand via `useItemImageSignedUrl`.
   */
  image_path: string | null;
  image_uploaded_at: string | null;
  created_at: string;
  updated_at: string;
}

const ITEM_IMAGES_BUCKET = 'item-images';
const ITEM_DOCUMENTS_BUCKET = 'item-documents';

export interface ItemSupplier {
  id: string;
  organization_id: string;
  item_id: string;
  vendor_id: string;
  supplier_part_number: string | null;
  /** Per purchase unit (per roll when `purchase_uom` is roll). Can be a fraction of a cent (migration 156). */
  unit_cost_cents: number | null;
  /** Date the price is good for: the order date of the PO it came from, or the day it was typed in. */
  unit_cost_as_of: string | null;
  /** PO line the price came from; null when it was typed in by hand (migration 156). */
  unit_cost_po_line_id: string | null;
  currency: string;
  lead_time_days: number | null;
  /** In purchase units. */
  min_order_qty: number | null;
  /** Pack / carton size in purchase units; suggested orders round up to it (migration 150). */
  order_multiple: number | null;
  /** Unit the vendor sells in (roll, box…); null = the item's stock unit (migration 153). */
  purchase_uom: string | null;
  /** "150" of "1 roll = 150 ft", as the vendor states it. Display only. */
  purchase_uom_size: number | null;
  /** "ft" of "1 roll = 150 ft". */
  purchase_uom_size_uom: string | null;
  /** Stock units in one purchase unit (1 roll of 150 ft stocked in inches = 1800). */
  units_per_purchase_uom: number;
  is_primary: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  vendors: { id: string; name: string; email: string | null; website: string | null } | null;
  /** Quantity price breaks, lowest `min_qty` first (migration 150). */
  item_supplier_price_breaks?: ItemSupplierPriceBreak[];
}

export interface ItemSupplierPriceBreak {
  id: string;
  min_qty: number;
  unit_cost_cents: number;
}

export interface ItemDocument {
  id: string;
  organization_id: string;
  item_id: string;
  kind: 'drawing' | 'spec_sheet' | 'photo' | 'manual' | 'other';
  label: string;
  url: string | null;
  storage_bucket: string | null;
  storage_path: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_by: string | null;
  created_at: string;
}

/**
 * Closed palette of tag swatches. Matches the CHECK constraint on
 * `public.item_tags.color` (migration 40); the client maps each token
 * to a Tailwind class set when rendering the badge.
 */
export const ITEM_TAG_COLORS = [
  'slate',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'indigo',
  'purple',
  'pink',
] as const;

export type ItemTagColor = (typeof ITEM_TAG_COLORS)[number];

export interface ItemTag {
  id: string;
  organization_id: string;
  name: string;
  color: ItemTagColor;
  created_at: string;
  updated_at: string;
}

/**
 * A workstation — an entry in the per-org managed vocabulary of stations
 * a component can be associated with (migration 94). Curated like tags
 * (case-insensitively unique per org) but without a colour: operators
 * pick from the list rather than free-typing so names stay canonical.
 */
export interface Workstation {
  id: string;
  organization_id: string;
  name: string;
  position: number;
  created_at: string;
  updated_at: string;
}

// -----------------------------------------------------------------------
// Items master
// -----------------------------------------------------------------------

export function useItems(params?: { kind?: ItemKind }) {
  return useQuery({
    queryKey: ['items', params],
    queryFn: async () => {
      let q = supabase
        .from('items' as never)
        // Hydrate the attached workstations in the same round-trip so the
        // grid can show + facet-filter by station without an N+1.
        .select('*, item_workstations(workstations(id, name))')
        .order('sku');
      if (params?.kind) {
        q = q.or(`kind.eq.${params.kind},additional_kinds.cs.{${params.kind}}`);
      }
      const { data, error } = await q;
      if (error) throw error;
      // Flatten the nested join projection down to a plain
      // `workstations: [{ id, name }]` array on each row.
      const rows = (data as unknown as Array<
        Item & {
          item_workstations?: Array<{ workstations: { id: string; name: string } | null }> | null;
        }
      >).map((row) => {
        const { item_workstations, ...rest } = row;
        const workstations = (item_workstations ?? [])
          .map((j) => j.workstations)
          .filter((w): w is { id: string; name: string } => w != null)
          .sort((a, b) => a.name.localeCompare(b.name));
        return { ...rest, workstations } as Item;
      });
      return rows;
    },
  });
}

export function useItem(id: string | undefined) {
  return useQuery({
    queryKey: ['item', id],
    enabled: !!id,
    queryFn: async () => {
      // `enabled: !!id` guards this branch from running without an id,
      // so the non-null assertion below is safe at runtime — Supabase's
      // `.eq()` types refuse `string | undefined` directly.
      const { data, error } = await supabase
        .from('items' as never)
        .select('*')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as unknown as Item;
    },
  });
}

/**
 * Input shape for creating a new item from the "Add item" dialog. We
 * keep this narrower than the full `Item` row — `organization_id`,
 * `is_active`, timestamps, and the image columns are all populated by
 * the DB (defaults / triggers / later upload flow), and the rest of
 * the optional fields here mirror what the dialog actually surfaces.
 */
export interface CreateItemInput {
  sku: string;
  name: string;
  kind: ItemKind;
  /** Extra types beyond `kind`. The create form's type checkboxes fill this. */
  additional_kinds?: ItemKind[];
  base_uom?: string;
  description?: string | null;
  category?: string | null;
  color?: string | null;
  status?: ItemStatus;
  std_cost_cents?: number | null;
  list_price_cents?: number | null;
  is_lot_tracked?: boolean;
  lead_time_text?: string | null;
  lead_time_worst_case_days?: number | null;
  notes?: string | null;
}

/**
 * Insert a new item row scoped to the caller's organization. RLS
 * (`items_write_admin`) restricts this to admin / super_admin, so the
 * UI gates the trigger on `canWrite(role, 'inventory')` to avoid a
 * confusing "permission denied" round-trip for managers + employees.
 */
export function useCreateItem() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateItemInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');

      const sku = input.sku.trim();
      const name = input.name.trim();
      if (!sku) throw new Error('SKU is required.');
      if (!name) throw new Error('Name is required.');

      const payload: Record<string, unknown> = {
        organization_id: profile.organization_id,
        sku,
        name,
        kind: input.kind,
        ...(input.additional_kinds && input.additional_kinds.length > 0
          ? { additional_kinds: input.additional_kinds }
          : {}),
        base_uom: input.base_uom?.trim() || 'each',
        description: input.description?.trim() ? input.description.trim() : null,
        category: input.category?.trim() ? input.category.trim() : null,
        color: input.color?.trim() ? input.color.trim() : null,
        status: input.status ?? 'concept',
        std_cost_cents: input.std_cost_cents ?? null,
        list_price_cents: input.list_price_cents ?? null,
        is_lot_tracked: input.is_lot_tracked ?? false,
        lead_time_text: input.lead_time_text?.trim() ? input.lead_time_text.trim() : null,
        lead_time_worst_case_days: input.lead_time_worst_case_days ?? null,
        notes: input.notes?.trim() ? input.notes.trim() : null,
      };

      const { data, error } = await supabase
        .from('items' as never)
        .insert(payload as never)
        .select('*')
        .single();
      if (error) {
        // Surface the unique-on-(org, sku) collision with a friendlier
        // message — Postgres reports it as `23505` and the operator
        // shouldn't have to decode that to know they need a new SKU.
        if ((error as { code?: string }).code === '23505') {
          throw new Error(`An item with SKU “${sku}” already exists.`);
        }
        throw error;
      }
      return data as unknown as Item;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
}

/**
 * Which related records to carry over when duplicating an item. Scalar
 * columns (name, kind, costs, etc.) are always copied; these flags
 * govern the extra child tables that take their own round-trips. Photos
 * default off because the most common reason to duplicate — a
 * colourway variant — gets *new* photos, and the SKU is
 * always required fresh because `(organization_id, sku)` is unique.
 */
export interface DuplicateItemInclude {
  tags?: boolean;
  suppliers?: boolean;
  bom?: boolean;
  photo?: boolean;
}

/**
 * Field overrides applied on top of the source item's copied scalars.
 * `sku` is required (the unique constraint forbids reusing the
 * source's); everything else falls back to the source value when
 * omitted. `null` clears a nullable column.
 */
export interface DuplicateItemOverrides {
  sku: string;
  name?: string;
  color?: string | null;
  category?: string | null;
  status?: ItemStatus;
  description?: string | null;
}

export interface DuplicateItemInput {
  sourceItemId: string;
  overrides: DuplicateItemOverrides;
  include?: DuplicateItemInclude;
}

export interface DuplicateItemResult {
  item: Item;
  /**
   * Non-fatal problems copying child records (tags / suppliers / BOM /
   * photo). The new item still exists; the caller surfaces these so the
   * operator knows what didn't carry over and can fix it by hand.
   */
  warnings: string[];
}

/**
 * Clone an existing item into a brand-new SKU, optionally carrying over
 * its tags, suppliers, BOM, and photo gallery.
 *
 * Built for the "colourway variant" workflow: an operator who already
 * has (e.g.) the Black model can duplicate everything that's shared and
 * then just tweak the colour + drop in a new photo, instead of
 * re-keying the whole 360 view.
 *
 * The scalar insert is the atomic core — if it fails (e.g. a duplicate
 * SKU) nothing else runs. Each child copy is then best-effort: a
 * failure there is collected as a warning rather than rolled back, so a
 * permission hiccup on one panel doesn't strand the operator with no
 * item at all. RLS (`items_write_admin` et al.) gates every write to
 * admin / super_admin, matching the `canWrite(role, 'inventory')` gate
 * on the call site.
 */
export function useDuplicateItem() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: DuplicateItemInput): Promise<DuplicateItemResult> => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const orgId = profile.organization_id;

      const sku = input.overrides.sku.trim();
      if (!sku) throw new Error('SKU is required.');

      // 1. Load the source row so we can copy its scalar columns.
      const { data: sourceRaw, error: srcErr } = await supabase
        .from('items' as never)
        .select('*')
        .eq('id', input.sourceItemId)
        .single();
      if (srcErr) throw srcErr;
      const source = sourceRaw as unknown as Item;

      // Helper so an override of `undefined` falls back to the source
      // value while an explicit `null` still clears the column.
      const pick = <T,>(override: T | undefined, fallback: T): T =>
        override === undefined ? fallback : override;

      const name = pick(input.overrides.name?.trim() || undefined, source.name);
      if (!name) throw new Error('Name is required.');

      // 2. Insert the clone. We deliberately omit the image columns
      //    here — photos (if requested) are copied in a follow-up
      //    step under the *new* item id so the storage key + RLS stay
      //    consistent.
      const payload: Record<string, unknown> = {
        organization_id: orgId,
        sku,
        name,
        kind: source.kind,
        additional_kinds: source.additional_kinds,
        base_uom: source.base_uom,
        description: pick(input.overrides.description, source.description),
        category: pick(input.overrides.category, source.category),
        color: pick(input.overrides.color, source.color),
        // A clone of an archived SKU is a new item, so it comes back as
        // Concept. Archiving is a separate step, and only after on-hand
        // is cleared.
        status: pick(
          input.overrides.status,
          source.status === 'archived' ? 'concept' : source.status,
        ),
        list_price_cents: source.list_price_cents,
        dealer_price_4_cents: source.dealer_price_4_cents,
        dealer_price_18_cents: source.dealer_price_18_cents,
        dealer_price_180_cents: source.dealer_price_180_cents,
        dealer_moq: source.dealer_moq,
        parent_sku: source.parent_sku,
        model_filters: source.model_filters,
        region: source.region,
        reorder_point: source.reorder_point,
        reorder_qty: source.reorder_qty,
        overstock_at: source.overstock_at,
        is_lot_tracked: source.is_lot_tracked,
        lead_time_text: source.lead_time_text,
        lead_time_worst_case_days: source.lead_time_worst_case_days,
        notes: source.notes,
      };

      const { data: createdRaw, error: insErr } = await supabase
        .from('items' as never)
        .insert(payload as never)
        .select('*')
        .single();
      if (insErr) {
        if ((insErr as { code?: string }).code === '23505') {
          throw new Error(`An item with SKU “${sku}” already exists.`);
        }
        throw insErr;
      }
      const created = createdRaw as unknown as Item;

      const include = input.include ?? {};
      const warnings: string[] = [];

      // 3. Tags — copy the assignment join rows verbatim.
      if (include.tags) {
        try {
          const { data: tagRows, error } = await supabase
            .from('item_tag_assignments' as never)
            .select('tag_id')
            .eq('item_id', source.id);
          if (error) throw error;
          const rows = (tagRows as Array<{ tag_id: string }> | null) ?? [];
          if (rows.length > 0) {
            const { error: insTagErr } = await supabase
              .from('item_tag_assignments' as never)
              .insert(
                rows.map((r) => ({
                  organization_id: orgId,
                  item_id: created.id,
                  tag_id: r.tag_id,
                })) as never,
              );
            if (insTagErr) throw insTagErr;
          }
        } catch {
          warnings.push('tags');
        }
      }

      // 4. Suppliers — copy every approved vendor link, preserving the
      //    primary flag (one source row is primary at most, so no
      //    partial-unique-index conflict on the copy).
      if (include.suppliers) {
        try {
          const { data: supRows, error } = await supabase
            .from('item_suppliers' as never)
            .select(
              'vendor_id, supplier_part_number, unit_cost_cents, currency, lead_time_days, min_order_qty, order_multiple, purchase_uom, purchase_uom_size, purchase_uom_size_uom, units_per_purchase_uom, is_primary, notes',
            )
            .eq('item_id', source.id);
          if (error) throw error;
          const rows =
            (supRows as Array<{
              vendor_id: string;
              supplier_part_number: string | null;
              unit_cost_cents: number | null;
              currency: string;
              lead_time_days: number | null;
              min_order_qty: number | null;
              order_multiple: number | null;
              purchase_uom: string | null;
              purchase_uom_size: number | null;
              purchase_uom_size_uom: string | null;
              units_per_purchase_uom: number;
              is_primary: boolean;
              notes: string | null;
            }> | null) ?? [];
          if (rows.length > 0) {
            const { error: insSupErr } = await supabase
              .from('item_suppliers' as never)
              .insert(
                rows.map((r) => ({
                  organization_id: orgId,
                  item_id: created.id,
                  vendor_id: r.vendor_id,
                  supplier_part_number: r.supplier_part_number,
                  unit_cost_cents: r.unit_cost_cents,
                  currency: r.currency,
                  lead_time_days: r.lead_time_days,
                  min_order_qty: r.min_order_qty,
                  order_multiple: r.order_multiple,
                  purchase_uom: r.purchase_uom,
                  purchase_uom_size: r.purchase_uom_size,
                  purchase_uom_size_uom: r.purchase_uom_size_uom,
                  units_per_purchase_uom: r.units_per_purchase_uom,
                  is_primary: r.is_primary,
                  notes: r.notes,
                })) as never,
              );
            if (insSupErr) throw insSupErr;
          }
        } catch {
          warnings.push('suppliers');
        }
      }

      // 5. BOM — recreate the source's active bom → versions → lines so
      //    the variant is "built from" the same components. We walk the
      //    tree top-down, mapping old version ids to freshly created
      //    ones as we go.
      if (include.bom) {
        try {
          const { data: bom, error } = await supabase
            .from('boms' as never)
            .select(
              'name, description, is_active, bom_versions(version, is_active, bom_lines(component_item_id, quantity, uom, scrap_pct))',
            )
            .eq('item_id', source.id)
            .eq('is_active', true)
            .maybeSingle();
          if (error) throw error;
          const bomRow = bom as unknown as {
            name: string;
            description: string | null;
            is_active: boolean;
            bom_versions: Array<{
              version: number;
              is_active: boolean;
              bom_lines: Array<{
                component_item_id: string;
                quantity: number;
                uom: string;
                scrap_pct: number;
              }>;
            }>;
          } | null;

          if (bomRow) {
            const { data: newBom, error: bomErr } = await supabase
              .from('boms' as never)
              .insert({
                organization_id: orgId,
                item_id: created.id,
                name: bomRow.name,
                description: bomRow.description,
                is_active: true,
              } as never)
              .select('id')
              .single();
            if (bomErr) throw bomErr;
            const newBomId = (newBom as unknown as { id: string }).id;

            for (const version of bomRow.bom_versions ?? []) {
              const { data: newVer, error: verErr } = await supabase
                .from('bom_versions' as never)
                .insert({
                  bom_id: newBomId,
                  version: version.version,
                  is_active: version.is_active,
                } as never)
                .select('id')
                .single();
              if (verErr) throw verErr;
              const newVersionId = (newVer as unknown as { id: string }).id;

              const lines = version.bom_lines ?? [];
              if (lines.length > 0) {
                const componentIds = [...new Set(lines.map((l) => l.component_item_id))];
                const { data: componentRows, error: componentErr } = await supabase
                  .from('items' as never)
                  .select('id, status')
                  .in('id', componentIds);
                if (componentErr) throw componentErr;
                const archivedComponents = new Set(
                  ((componentRows ?? []) as unknown as Array<{ id: string; status: string }>)
                    .filter((row) => row.status === 'archived')
                    .map((row) => row.id),
                );
                const kept = lines.filter((l) => !archivedComponents.has(l.component_item_id));
                if (kept.length < lines.length && !warnings.includes('archived components')) {
                  warnings.push('archived components');
                }
                if (kept.length > 0) {
                  const { error: lineErr } = await supabase
                    .from('bom_lines' as never)
                    .insert(
                      kept.map((l) => ({
                        bom_version_id: newVersionId,
                        component_item_id: l.component_item_id,
                        quantity: l.quantity,
                        uom: l.uom,
                        scrap_pct: l.scrap_pct,
                      })) as never,
                    );
                  if (lineErr) throw lineErr;
                }
              }
            }
          }
        } catch {
          warnings.push('BOM');
        }
      }

      // 6. Photos — copy every gallery object into the new item's key
      //    space, register it, and keep the same primary. Off by default;
      //    the colourway workflow swaps in fresh photos.
      if (include.photo) {
        try {
          const { data: galleryRaw, error: galleryErr } = await supabase
            .from('item_images' as never)
            .select('*')
            .eq('item_id', source.id)
            .order('position', { ascending: true })
            .order('created_at', { ascending: true });
          if (galleryErr) throw galleryErr;
          const gallery = galleryRaw as unknown as ItemImage[];
          let newPrimary: string | null = null;
          let firstCopied: string | null = null;
          for (const img of gallery) {
            const filename = img.storage_path.split('/').pop() ?? 'photo';
            const newPath = buildItemImagePath({
              organization_id: orgId,
              item_id: created.id,
              filename,
            });
            const { error: copyErr } = await supabase.storage
              .from(ITEM_IMAGES_BUCKET)
              .copy(img.storage_path, newPath);
            if (copyErr) throw copyErr;
            const { error: rowErr } = await supabase.from('item_images' as never).insert({
              organization_id: orgId,
              item_id: created.id,
              storage_path: newPath,
              caption: img.caption,
              position: img.position,
              mime_type: img.mime_type,
              size_bytes: img.size_bytes,
              uploaded_by: profile.id,
            } as never);
            if (rowErr) throw rowErr;
            firstCopied ??= newPath;
            if (img.storage_path === source.image_path) newPrimary = newPath;
          }
          newPrimary ??= firstCopied;
          if (newPrimary) {
            await setItemPrimaryImage(created.id, newPrimary);
            created.image_path = newPrimary;
          }
        } catch {
          warnings.push('photos');
        }
      }

      return { item: created, warnings };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ITEM_PRIMARY_SUPPLIERS_KEY });
      qc.setQueryData(['item', result.item.id], result.item);
    },
  });
}

/**
 * Input shape for a single row of the bulk spreadsheet import. Wider
 * than `CreateItemInput` because the spreadsheet template also collects
 * `reorder_point` / `reorder_qty` (handy to seed at onboarding time)
 * that the single-item dialog leaves for the detail page.
 */
export interface BulkCreateItemInput {
  sku: string;
  name: string;
  kind: ItemKind;
  base_uom?: string;
  description?: string | null;
  category?: string | null;
  color?: string | null;
  status?: ItemStatus;
  std_cost_cents?: number | null;
  list_price_cents?: number | null;
  is_lot_tracked?: boolean;
  reorder_point?: number | null;
  reorder_qty?: number | null;
  lead_time_text?: string | null;
  lead_time_worst_case_days?: number | null;
  notes?: string | null;
  /**
   * Free-text supplier name from the spreadsheet. On import we match it
   * (via a normalised comparison) against the org's existing vendors and
   * link the survivor; an unmatched name mints a new vendor. Blank skips
   * the supplier link entirely.
   */
  supplier?: string | null;
}

/** Per-row outcome of a bulk import so the UI can report what landed. */
export interface BulkCreateItemResult {
  created: number;
  failures: Array<{ sku: string; name: string; error: string }>;
}

/**
 * Insert many items from a parsed spreadsheet. We insert row-by-row
 * (rather than one batch `insert([...])`) so a single bad row — a
 * duplicate SKU already in the DB, say — doesn't roll back the whole
 * upload and so we can report exactly which rows failed and why. RLS
 * (`items_write_admin`) still gates this to admin / super_admin, mirrored
 * by the UI gate that surfaces the import entry point.
 */
export function useBulkCreateItems() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (rows: BulkCreateItemInput[]): Promise<BulkCreateItemResult> => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const orgId = profile.organization_id;
      if (rows.length === 0) return { created: 0, failures: [] };

      let created = 0;
      const failures: BulkCreateItemResult['failures'] = [];

      // --- Supplier matching setup -------------------------------------
      // Load the org's existing vendors once and key them by a normalised
      // form of their name so a spreadsheet supplier resolves to the right
      // row even when the casing / punctuation / legal suffix differs
      // ("Acme, Inc." → "Acme Inc" → "acme"). The map is mutated as we mint
      // new vendors so two rows naming the same new supplier share one row
      // instead of racing the unique index into a duplicate.
      const vendorByKey = new Map<string, string>();
      const anySupplier = rows.some((r) => r.supplier?.trim());
      if (anySupplier) {
        const { data: vendorRows, error: vendorErr } = await supabase
          .from('vendors')
          .select('id, name')
          .eq('organization_id', orgId);
        if (vendorErr) throw vendorErr;
        for (const v of (vendorRows as Array<{ id: string; name: string }> | null) ?? []) {
          const key = normalizeVendorName(v.name);
          if (key && !vendorByKey.has(key)) vendorByKey.set(key, v.id);
        }
      }

      /**
       * Resolve a free-text supplier name to a vendor id, creating the
       * vendor when no normalised match exists. Returns null for blank
       * input. Throws on a hard DB error so the row is reported failed.
       */
      const resolveVendor = async (raw: string): Promise<string | null> => {
        const name = raw.trim();
        if (!name) return null;
        const key = normalizeVendorName(name);
        const existing = vendorByKey.get(key);
        if (existing) return existing;

        const { data, error } = await supabase
          .from('vendors')
          .insert({ organization_id: orgId, name } as never)
          .select('id')
          .single();
        if (error) throw error;
        const id = (data as unknown as { id: string }).id;
        if (key) vendorByKey.set(key, id);
        return id;
      };

      for (const input of rows) {
        const sku = input.sku.trim();
        const name = input.name.trim();
        if (!sku || !name) {
          failures.push({ sku, name, error: 'SKU and Name are required.' });
          continue;
        }

        const payload: Record<string, unknown> = {
          organization_id: orgId,
          sku,
          name,
          kind: input.kind,
          base_uom: input.base_uom?.trim() || 'each',
          description: input.description?.trim() ? input.description.trim() : null,
          category: input.category?.trim() ? input.category.trim() : null,
          color: input.color?.trim() ? input.color.trim() : null,
          status: input.status ?? 'concept',
          std_cost_cents: input.std_cost_cents ?? null,
          list_price_cents: input.list_price_cents ?? null,
          is_lot_tracked: input.is_lot_tracked ?? false,
          reorder_point: input.reorder_point ?? null,
          reorder_qty: input.reorder_qty ?? null,
          lead_time_text: input.lead_time_text?.trim() ? input.lead_time_text.trim() : null,
          lead_time_worst_case_days: input.lead_time_worst_case_days ?? null,
          notes: input.notes?.trim() ? input.notes.trim() : null,
        };

        const { data: insertedRaw, error } = await supabase
          .from('items' as never)
          .insert(payload as never)
          .select('id')
          .single();
        if (error) {
          const code = (error as { code?: string }).code;
          let message = error.message ?? 'Insert failed.';
          if (code === '23505') message = `An item with SKU “${sku}” already exists.`;
          else if (code === '23503') message = `Unknown kind “${input.kind}”.`;
          failures.push({ sku, name, error: message });
          continue;
        }
        created += 1;

        // Link the supplier as this item's primary approved vendor. This
        // is best-effort: the item already exists, so a supplier hiccup
        // is surfaced as a per-row warning rather than un-creating the SKU.
        if (input.supplier?.trim()) {
          const itemId = (insertedRaw as unknown as { id: string }).id;
          try {
            const vendorId = await resolveVendor(input.supplier);
            if (vendorId) {
              const { error: linkErr } = await supabase
                .from('item_suppliers' as never)
                .insert({
                  organization_id: orgId,
                  item_id: itemId,
                  vendor_id: vendorId,
                  lead_time_days: input.lead_time_worst_case_days ?? null,
                  is_primary: true,
                } as never);
              if (linkErr) throw linkErr;
            }
          } catch (err) {
            failures.push({
              sku,
              name,
              error: `Item created, but linking supplier “${input.supplier.trim()}” failed: ${
                err instanceof Error ? err.message : 'unknown error'
              }`,
            });
          }
        }
      }

      return { created, failures };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['vendors'] });
      qc.invalidateQueries({ queryKey: ['item-suppliers'] });
      qc.invalidateQueries({ queryKey: ['supplier-part-numbers'] });
      qc.invalidateQueries({ queryKey: ['item-cost-summaries'] });
    },
  });
}

/**
 * Normalise a vendor name into a comparison key for the bulk-import
 * supplier matcher. Lowercases, strips accents, drops punctuation,
 * collapses whitespace, and removes common legal suffixes (Inc, LLC,
 * Ltd, Co, Corp, Company) so "Acme, Inc.", "ACME Inc" and "acme"
 * all collapse to the same key — the "strong matching" that keeps the
 * vendor directory from forking into near-duplicate rows on import.
 */
export function normalizeVendorName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]+/g, ' ')
    .replace(/\b(inc|incorporated|llc|l l c|ltd|limited|co|corp|corporation|company)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Turn a pasted storefront link into the absolute http(s) URL the
 * `items_*_url_format` checks accept (migration 163). A bare
 * "virro.com/products/renew" gets https:// in front; empty clears it.
 */
export function normalizeListingUrl(raw: string | null, label: string): string | null {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    throw new Error(`${label} isn't a valid web address.`);
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error(`${label} must be an http(s) link.`);
  }
  if (!parsed.hostname.includes('.') || /\s/.test(withScheme)) {
    throw new Error(`${label} isn't a valid web address.`);
  }
  if (withScheme.length > 2048) throw new Error(`${label} is too long.`);
  return withScheme;
}

/**
 * Patch shape for `useUpdateItem`. Every field is optional — callers
 * pass only what changed so single-field inline edits don't have to
 * round-trip the whole row. `null` is a meaningful "clear this column"
 * for the nullable text/number fields; `undefined` means "leave it
 * alone".
 */
export interface UpdateItemPatch {
  sku?: string;
  name?: string;
  kind?: ItemKind;
  additional_kinds?: ItemKind[];
  base_uom?: string;
  description?: string | null;
  category?: string | null;
  color?: string | null;
  status?: ItemStatus;
  std_cost_cents?: number | null;
  list_price_cents?: number | null;
  dealer_price_4_cents?: number | null;
  dealer_price_18_cents?: number | null;
  dealer_price_180_cents?: number | null;
  dealer_moq?: number | null;
  parent_sku?: string | null;
  model_filters?: string[];
  region?: string | null;
  shopify_url?: string | null;
  amazon_url?: string | null;
  reorder_point?: number | null;
  reorder_qty?: number | null;
  overstock_at?: number | null;
  is_made_in_house?: boolean;
  default_work_center_id?: string | null;
  purchase_url?: string | null;
  is_lot_tracked?: boolean;
  is_serialized?: boolean;
  lead_time_text?: string | null;
  lead_time_worst_case_days?: number | null;
  notes?: string | null;
}

/**
 * Apply a partial update to an existing item. Powers the inline-edit
 * UI on the Items grid + the 360 detail page so an admin can fix a
 * SKU, retitle a product, or flip a status without leaving the row.
 *
 * Cross-table propagation is automatic at the schema level: every
 * downstream table (BOM lines, PO lines, SO lines, work orders,
 * inventory levels, stock movements, item suppliers, item documents)
 * references `items.id` (uuid PK), not `items.sku` — see migration 08.
 * Renaming a SKU is therefore a single-row UPDATE; everything that
 * joins back to `items` reads the new value on its next fetch. We
 * just bust the relevant React Query caches so the UI reflects it
 * immediately too.
 *
 * RLS (`items_write_admin`) restricts this to admin / super_admin so
 * the call sites gate the trigger on `canWrite(role, 'inventory')`.
 */
export function useUpdateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: UpdateItemPatch }) => {
      const cleaned: Record<string, unknown> = {};
      // Walk the patch by hand so we can: (a) trim free-text fields
      // (and require sku/name to be non-empty after trimming), (b) skip
      // explicit `undefined` values so a partial patch stays partial,
      // and (c) preserve `null` for nullable columns so "clear it" still
      // round-trips.
      for (const [key, raw] of Object.entries(input.patch) as Array<
        [keyof UpdateItemPatch, unknown]
      >) {
        if (raw === undefined) continue;
        if (key === 'sku' || key === 'name') {
          const trimmed = typeof raw === 'string' ? raw.trim() : '';
          if (!trimmed) {
            throw new Error(`${key === 'sku' ? 'SKU' : 'Name'} is required.`);
          }
          cleaned[key] = trimmed;
        } else if (key === 'base_uom') {
          const trimmed = typeof raw === 'string' ? raw.trim() : '';
          if (!trimmed) throw new Error('UoM Produced is required.');
          cleaned[key] = trimmed;
        } else if (
          key === 'category' ||
          key === 'color' ||
          key === 'description' ||
          key === 'lead_time_text' ||
          key === 'notes' ||
          key === 'parent_sku' ||
          key === 'purchase_url' ||
          key === 'region'
        ) {
          if (raw === null) {
            cleaned[key] = null;
          } else if (typeof raw === 'string') {
            const trimmed = raw.trim();
            cleaned[key] = trimmed === '' ? null : trimmed;
          }
        } else if (key === 'shopify_url' || key === 'amazon_url') {
          cleaned[key] = normalizeListingUrl(
            typeof raw === 'string' ? raw : null,
            key === 'shopify_url' ? 'Shopify link' : 'Amazon link',
          );
        } else {
          cleaned[key] = raw;
        }
      }

      if (Object.keys(cleaned).length === 0) {
        // Nothing to do — return the current row so optimistic UI
        // stays in sync without a wasted round-trip.
        const { data, error } = await supabase
          .from('items' as never)
          .select('*')
          .eq('id', input.id)
          .single();
        if (error) throw error;
        return data as unknown as Item;
      }

      const { data, error } = await supabase
        .from('items' as never)
        .update(cleaned as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) {
        // Same friendly mapping as `useCreateItem` for the
        // unique-on-(org, sku) collision so the operator sees a
        // human-readable hint instead of `23505`.
        if ((error as { code?: string }).code === '23505') {
          const sku = typeof cleaned.sku === 'string' ? cleaned.sku : null;
          throw new Error(
            sku
              ? `An item with SKU “${sku}” already exists.`
              : 'That value collides with another item — try a different one.',
          );
        }
        throw error;
      }
      return data as unknown as Item;
    },
    onSuccess: (data, variables) => {
      // Prime the detail cache with the freshly returned row so the
      // inline editor reads back the canonical values (status enum
      // normalisation, trigger-touched columns like is_active /
      // updated_at) without an extra fetch.
      qc.setQueryData(['item', variables.id], data);
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['item', variables.id] });

      // Turning on make-in-house or raising the reorder point can draft a
      // production order server-side.
      if (
        variables.patch.is_made_in_house !== undefined ||
        variables.patch.reorder_point !== undefined ||
        variables.patch.default_work_center_id !== undefined
      ) {
        qc.invalidateQueries({ queryKey: ['production-schedules'] });
        qc.invalidateQueries({ queryKey: ['item-production-orders', variables.id] });
      }

      // SKU + name are the two columns that show up in *joined* views
      // throughout the app (inventory levels, stock movements, BOMs,
      // sales/purchase orders, work-order consumption, etc.). Bust those
      // caches so a rename propagates to every grid that reads the
      // joined value, even ones already mounted on screen.
      const renamed = variables.patch.sku !== undefined || variables.patch.name !== undefined;
      if (renamed) {
        qc.invalidateQueries({ queryKey: ['inventory-levels'] });
        qc.invalidateQueries({ queryKey: ['stock-movements'] });
        qc.invalidateQueries({ queryKey: ['bom-for-item'] });
        qc.invalidateQueries({ queryKey: ['boms-containing-item'] });
        qc.invalidateQueries({ queryKey: ['item-suppliers'] });
        qc.invalidateQueries({ queryKey: ['item-so-lines'] });
        qc.invalidateQueries({ queryKey: ['item-po-lines'] });
        qc.invalidateQueries({ queryKey: ['item-wo-producing'] });
        qc.invalidateQueries({ queryKey: ['item-wo-consuming'] });
        qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      }
    },
  });
}

/**
 * Hard-delete one or more items by id. Powers the bulk "Delete" action
 * on the Items grid (select rows → Delete).
 *
 * Most downstream tables (BOM lines, PO/SO lines, work orders, inventory
 * levels, stock movements, item suppliers/documents) FK to `items.id`
 * with `ON DELETE RESTRICT`, so an item that's referenced anywhere can't
 * be removed. Postgres reports that as `23503`; we translate it into a
 * human-readable hint instead of leaking the raw code. The list operation
 * is best-effort per the RLS `items_write_admin` policy (admin /
 * super_admin only) — the call site gates the entry point on
 * `canWrite(role, 'inventory')`.
 */
export function useDeleteItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[] }) => {
      const ids = input.ids.filter(Boolean);
      if (ids.length === 0) return { deleted: 0 };

      const { error } = await supabase
        .from('items' as never)
        .delete()
        .in('id', ids);
      if (error) {
        const code = (error as { code?: string }).code;
        if (code === '23503') {
          throw new Error(
            ids.length === 1
              ? 'This item is still referenced by a BOM, order, or stock record and can’t be deleted. Set its status to “Archived” instead.'
              : 'One or more selected items are still referenced by a BOM, order, or stock record and can’t be deleted. Set those items to “Archived” instead.',
          );
        }
        throw error;
      }
      return { deleted: ids.length };
    },
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      for (const id of variables.ids) {
        qc.removeQueries({ queryKey: ['item', id] });
      }
    },
  });
}

// -----------------------------------------------------------------------
// Locations (warehouse / production floor / transit / quarantine)
//
// The location master is the set of physical (or logical) buckets that
// on-hand inventory is tracked against. Every stock movement names a
// from / to location, so this table is the prerequisite for any
// inventory operation. Writes are gated to admin / super_admin by the
// `loc_write_admin` RLS policy (migration 08); the UI mirrors that with
// `canWrite(role, 'inventory')`.
// -----------------------------------------------------------------------

/** Canonical location kinds, in picker order. Stored as free text on the
 *  row (migration 08 leaves `kind` untyped) but the UI offers this fixed
 *  list so the four buckets stay consistent across the org. */
export const LOCATION_KINDS = [
  'warehouse',
  'production_floor',
  'transit',
  'quarantine',
] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number];

export const LOCATION_KIND_LABELS: Record<LocationKind, string> = {
  warehouse: 'Warehouse',
  production_floor: 'Production floor',
  transit: 'Transit',
  quarantine: 'Quarantine',
};

export interface LocationAddress {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

export interface Location {
  id: string;
  organization_id: string;
  code: string;
  name: string;
  kind: string | null;
  address: LocationAddress | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Every location in the caller's org, ordered by code. By default only
 * active locations are returned (the movement / adjustment pickers don't
 * want to offer a decommissioned bin); pass `includeInactive` for the
 * Locations management grid which shows everything.
 */
export function useLocations(opts?: { includeInactive?: boolean }) {
  const includeInactive = opts?.includeInactive ?? false;
  return useQuery({
    queryKey: ['locations', includeInactive],
    queryFn: async () => {
      let q = supabase.from('locations' as never).select('*').order('code');
      if (!includeInactive) q = q.eq('is_active', true);
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as Location[];
    },
  });
}

export interface CreateLocationInput {
  code: string;
  name: string;
  kind?: LocationKind | null;
  address?: LocationAddress | null;
  is_active?: boolean;
}

/**
 * Create a location. RLS (`loc_write_admin`) restricts this to admin /
 * super_admin; the unique-on-(org, code) collision is surfaced with a
 * friendly message.
 */
export function useCreateLocation() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateLocationInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const code = input.code.trim();
      const name = input.name.trim();
      if (!code) throw new Error('Code is required.');
      if (!name) throw new Error('Name is required.');

      const { data, error } = await supabase
        .from('locations' as never)
        .insert({
          organization_id: profile.organization_id,
          code,
          name,
          kind: input.kind ?? null,
          address: cleanAddress(input.address),
          is_active: input.is_active ?? true,
        } as never)
        .select('*')
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error(`A location with code “${code}” already exists.`);
        }
        throw error;
      }
      return data as unknown as Location;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  });
}

export interface UpdateLocationPatch {
  code?: string;
  name?: string;
  kind?: LocationKind | null;
  address?: LocationAddress | null;
  is_active?: boolean;
}

/** Update a location (rename, recategorise, change address, de/reactivate). */
export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: UpdateLocationPatch }) => {
      const cleaned: Record<string, unknown> = {};
      const { patch } = input;
      if (patch.code !== undefined) {
        const trimmed = patch.code.trim();
        if (!trimmed) throw new Error('Code is required.');
        cleaned.code = trimmed;
      }
      if (patch.name !== undefined) {
        const trimmed = patch.name.trim();
        if (!trimmed) throw new Error('Name is required.');
        cleaned.name = trimmed;
      }
      if (patch.kind !== undefined) cleaned.kind = patch.kind;
      if (patch.address !== undefined) cleaned.address = cleanAddress(patch.address);
      if (patch.is_active !== undefined) cleaned.is_active = patch.is_active;

      if (Object.keys(cleaned).length === 0) {
        const { data, error } = await supabase
          .from('locations' as never)
          .select('*')
          .eq('id', input.id)
          .single();
        if (error) throw error;
        return data as unknown as Location;
      }

      const { data, error } = await supabase
        .from('locations' as never)
        .update(cleaned as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error('A location with that code already exists.');
        }
        throw error;
      }
      return data as unknown as Location;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations'] });
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
    },
  });
}

/** Drop empty address fields so we store `null` rather than a blank object. */
function cleanAddress(address: LocationAddress | null | undefined): LocationAddress | null {
  if (!address) return null;
  const entries = Object.entries(address)
    .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v] as const)
    .filter(([, v]) => v != null && v !== '');
  return entries.length > 0 ? (Object.fromEntries(entries) as LocationAddress) : null;
}

// -----------------------------------------------------------------------
// Lots / batches
//
// Lot-tracked items (`items.is_lot_tracked`) carry a `lot_id` on every
// inventory level + stock movement. The movement / adjustment dialogs
// find-or-create a lot by (item, lot_number) so the operator only has to
// type the number.
// -----------------------------------------------------------------------

export interface Lot {
  id: string;
  organization_id: string;
  item_id: string;
  lot_number: string;
  produced_at: string | null;
  expires_at: string | null;
  vendor_id: string | null;
  notes: string | null;
  status: string;
  created_at: string;
}

/** Lots for one item (or all lots in the org when `itemId` is omitted). */
export function useLots(itemId?: string) {
  return useQuery({
    queryKey: ['lots', itemId ?? null],
    queryFn: async () => {
      let q = supabase.from('lots' as never).select('*').order('lot_number');
      if (itemId) q = q.eq('item_id', itemId);
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as Lot[];
    },
  });
}

/**
 * Resolve a (item, lot_number) pair to a lot id, creating the lot row
 * when it doesn't exist yet. Mirrors the find-or-create walk in
 * `useAddItemToBom`, with a retry on the unique-constraint race.
 */
async function findOrCreateLot(
  organizationId: string,
  itemId: string,
  lotNumber: string,
): Promise<string> {
  const existing = await supabase
    .from('lots' as never)
    .select('id')
    .eq('item_id', itemId)
    .eq('lot_number', lotNumber)
    .maybeSingle();
  if (existing.error) throw existing.error;
  if (existing.data) return (existing.data as unknown as { id: string }).id;

  const { data, error } = await supabase
    .from('lots' as never)
    .insert({ organization_id: organizationId, item_id: itemId, lot_number: lotNumber } as never)
    .select('id')
    .single();
  if (error) {
    if ((error as { code?: string }).code === '23505') {
      const retry = await supabase
        .from('lots' as never)
        .select('id')
        .eq('item_id', itemId)
        .eq('lot_number', lotNumber)
        .maybeSingle();
      if (retry.data) return (retry.data as unknown as { id: string }).id;
    }
    throw error;
  }
  return (data as unknown as { id: string }).id;
}

// -----------------------------------------------------------------------
// On-hand inventory levels (read-only rollup, maintained by trigger)
// -----------------------------------------------------------------------

export interface InventoryLevelRow {
  id: string;
  organization_id: string;
  item_id: string;
  location_id: string;
  lot_id: string | null;
  quantity: number;
  updated_at: string;
  locations: { name: string; code: string; kind: string | null } | null;
  items: {
    sku: string;
    name: string;
    base_uom: string;
    reorder_point: number | null;
    reorder_qty: number | null;
  } | null;
  lots: { lot_number: string; expires_at: string | null; status: string } | null;
}

export function useInventoryLevels(itemId?: string) {
  return useQuery({
    queryKey: ['inventory-levels', itemId],
    queryFn: async () => {
      let q = supabase
        .from('inventory_levels' as never)
        .select(
          '*, locations(name, code, kind), items(sku, name, base_uom, reorder_point, reorder_qty), lots(lot_number, expires_at, status)',
        );
      if (itemId) q = q.eq('item_id', itemId);
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as InventoryLevelRow[];
    },
  });
}

// -----------------------------------------------------------------------
// Stock movements (the source of truth for on-hand)
// -----------------------------------------------------------------------

export const STOCK_MOVEMENT_TYPES = [
  'receipt',
  'issue',
  'transfer',
  'adjustment',
  'cycle_count',
  'production_consume',
  'production_produce',
] as const;
export type StockMovementType = (typeof STOCK_MOVEMENT_TYPES)[number];

export const STOCK_MOVEMENT_TYPE_LABELS: Record<StockMovementType, string> = {
  receipt: 'Receipt',
  issue: 'Issue',
  transfer: 'Transfer',
  adjustment: 'Adjustment',
  cycle_count: 'Cycle count',
  production_consume: 'Production consume',
  production_produce: 'Production produce',
};

/** Reason picklist for adjustment / cycle-count movements (migration 90). */
export const ADJUSTMENT_REASONS = [
  'count_correction',
  'damage',
  'scrap',
  'expiry',
  'shrinkage',
  'found',
  'return_to_stock',
  'other',
] as const;
export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number];

export const ADJUSTMENT_REASON_LABELS: Record<AdjustmentReason, string> = {
  count_correction: 'Count correction',
  damage: 'Damage',
  scrap: 'Scrap',
  expiry: 'Expiry',
  shrinkage: 'Shrinkage',
  found: 'Found stock',
  return_to_stock: 'Return to stock',
  other: 'Other',
};

export interface StockMovementRow {
  id: string;
  item_id: string;
  type: StockMovementType;
  quantity: number;
  memo: string | null;
  reason_code: string | null;
  reference_type: string | null;
  reference_id: string | null;
  from_location_id: string | null;
  to_location_id: string | null;
  lot_id: string | null;
  created_at: string;
  status?: 'posted' | 'reversed';
  reverses_movement_id?: string | null;
  reversed_at?: string | null;
  reversal_reason?: string | null;
  items: { sku: string; name: string; base_uom: string } | null;
  from: { name: string; code: string; kind: LocationKind } | null;
  to: { name: string; code: string; kind: LocationKind } | null;
  lots: { lot_number: string } | null;
}

export function useStockMovements(params?: {
  limit?: number;
  itemId?: string;
  types?: StockMovementType[];
  locationId?: string;
}) {
  return useQuery({
    queryKey: ['stock-movements', params],
    queryFn: async () => {
      let q = supabase
        .from('stock_movements' as never)
        .select(
          '*, items(sku, name, base_uom), from:from_location_id(name, code, kind), to:to_location_id(name, code, kind), lots(lot_number)',
        )
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(params?.limit ?? 200);
      if (params?.itemId) q = q.eq('item_id', params.itemId);
      if (params?.types && params.types.length > 0) q = q.in('type', params.types);
      if (params?.locationId) {
        q = q.or(
          `from_location_id.eq.${params.locationId},to_location_id.eq.${params.locationId}`,
        );
      }
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as StockMovementRow[];
    },
  });
}

/** The document a stock movement was posted from, with a link to open it. */
export interface StockMovementSource {
  label: string;
  href: string;
}

const SOURCE_LOOKUP_CHUNK = 100;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

async function selectByIds<T>(table: string, columns: string, ids: string[]): Promise<T[]> {
  const rows: T[] = [];
  for (let i = 0; i < ids.length; i += SOURCE_LOOKUP_CHUNK) {
    const { data, error } = await supabase
      .from(table as never)
      .select(columns)
      .in('id', ids.slice(i, i + SOURCE_LOOKUP_CHUNK));
    if (error) throw error;
    rows.push(...((data ?? []) as unknown as T[]));
  }
  return rows;
}

/**
 * Resolve `reference_type` / `reference_id` on stock movements to the
 * production form, receipt, return, or work order they came from. Keyed
 * by `${reference_type}:${reference_id}`. Reversal rows carry the original's
 * reference, so they resolve to the same document.
 */
export function useStockMovementSources(movements: StockMovementRow[] | undefined) {
  const refs = useMemo(() => {
    const byType = new Map<string, Set<string>>();
    for (const m of movements ?? []) {
      if (!m.reference_type || !m.reference_id || !UUID_RE.test(m.reference_id)) continue;
      if (!byType.has(m.reference_type)) byType.set(m.reference_type, new Set());
      byType.get(m.reference_type)!.add(m.reference_id);
    }
    const ids = (type: string) => [...(byType.get(type) ?? [])].sort();
    return {
      production_form: ids('production_form'),
      goods_receipt: ids('goods_receipt'),
      return: ids('return'),
      work_order: ids('work_order'),
    };
  }, [movements]);

  return useQuery({
    queryKey: ['stock-movement-sources', refs],
    enabled: !!movements,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [forms, receipts, returns, workOrders] = await Promise.all([
        selectByIds<{ id: string; production_number: string }>(
          'production_forms',
          'id, production_number',
          refs.production_form,
        ),
        selectByIds<{
          id: string;
          receipt_number: string | null;
          purchase_order_id: string;
          purchase_orders: { po_number: string } | null;
        }>(
          'goods_receipts',
          'id, receipt_number, purchase_order_id, purchase_orders(po_number)',
          refs.goods_receipt,
        ),
        selectByIds<{ id: string; rma_number: string }>('returns', 'id, rma_number', refs.return),
        selectByIds<{ id: string; wo_number: string }>(
          'work_orders',
          'id, wo_number',
          refs.work_order,
        ),
      ]);

      const map = new Map<string, StockMovementSource>();
      for (const f of forms) {
        map.set(`production_form:${f.id}`, {
          label: f.production_number,
          href: `/mfg/production?form=${f.id}`,
        });
      }
      for (const r of receipts) {
        const po = r.purchase_orders?.po_number;
        map.set(`goods_receipt:${r.id}`, {
          label: [r.receipt_number, po].filter(Boolean).join(' · ') || 'Receipt',
          href: `/procurement/purchase-orders?po=${r.purchase_order_id}`,
        });
      }
      for (const r of returns) {
        map.set(`return:${r.id}`, { label: r.rma_number, href: `/sales/returns?rma=${r.id}` });
      }
      for (const w of workOrders) {
        map.set(`work_order:${w.id}`, { label: w.wo_number, href: '/mfg/work-orders' });
      }
      return map;
    },
  });
}

export interface RecordStockMovementInput {
  itemId: string;
  type: StockMovementType;
  /** Positive integer in the item's base UoM. */
  quantity: number;
  fromLocationId?: string | null;
  toLocationId?: string | null;
  /** Lot number to find-or-create for the item (lot-tracked items only). */
  lotNumber?: string | null;
  reasonCode?: string | null;
  memo?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
}

/**
 * Record a stock movement. This is the ONLY supported way to change
 * on-hand: the `tg_apply_stock_movement` trigger (migration 08) maintains
 * `inventory_levels` off every insert, and direct writes to
 * `inventory_levels` are blocked by RLS. Quantity is always a positive
 * base-UoM integer; direction is expressed by which of from / to is set:
 *
 *   * receipt  → to only          (stock comes in)
 *   * issue    → from only        (stock goes out)
 *   * transfer → from + to        (stock moves between locations)
 *   * adjustment / cycle_count → to (increase) or from (decrease)
 *
 * `sm_write_org` RLS lets any org member insert, but the UI still gates
 * the entry points on `canWrite(role, 'inventory')` so the surface
 * matches the rest of the inventory module.
 */
export async function postStockMovement(
  organizationId: string,
  input: RecordStockMovementInput,
): Promise<{ id: string }> {
  if (!input.itemId) throw new Error('Pick an item first.');
  if (!Number.isFinite(input.quantity) || input.quantity <= 0) {
    throw new Error('Quantity must be greater than zero.');
  }
  const fromId = input.fromLocationId || null;
  const toId = input.toLocationId || null;
  if (!fromId && !toId) throw new Error('Pick at least one location.');
  if (fromId && toId && fromId === toId) {
    throw new Error('The from and to locations must be different.');
  }

  let lotId: string | null = null;
  const lotNumber = input.lotNumber?.trim();
  if (lotNumber) {
    lotId = await findOrCreateLot(organizationId, input.itemId, lotNumber);
  }

  const { data: auth } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('stock_movements' as never)
    .insert({
      organization_id: organizationId,
      item_id: input.itemId,
      type: input.type,
      quantity: Math.round(input.quantity),
      from_location_id: fromId,
      to_location_id: toId,
      lot_id: lotId,
      reason_code: input.reasonCode ?? null,
      memo: input.memo?.trim() || null,
      reference_type: input.referenceType ?? null,
      reference_id: input.referenceId ?? null,
      actor_id: auth?.user?.id ?? null,
    } as never)
    .select('*')
    .single();
  if (error) throw error;
  return data as unknown as { id: string };
}

export function useRecordStockMovement() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: RecordStockMovementInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      return postStockMovement(profile.organization_id, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['lots'] });
    },
  });
}

export interface ArchiveInventoryBalance {
  id: string;
  locationId: string;
  locationName: string;
  lotId: string | null;
  lotNumber: string | null;
  quantity: number;
}

export interface OpenPurchaseOrderHold {
  poNumber: string;
  status: string;
  outstanding: number;
}

export interface ArchiveReadiness {
  balances: ArchiveInventoryBalance[];
  openPurchaseOrders: OpenPurchaseOrderHold[];
}

// Cancelled orders will not be received. A delivered order drops out on
// its own because nothing is still outstanding. `received` / `closed` are
// retired labels from before the procurement status rename.
const CLOSED_PO_STATUSES = new Set(['cancelled']);

function embeddedOne<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

/**
 * On-hand buckets and open purchase orders that have to be dealt with
 * before an item can leave the operational catalog. A zero-quantity
 * rollup row is ignored. Open orders are a warning: receiving them
 * after archive is rejected.
 */
export async function fetchArchiveReadiness(itemId: string): Promise<ArchiveReadiness> {
  const [levelsRes, poRes] = await Promise.all([
    supabase
      .from('inventory_levels' as never)
      .select('id, location_id, lot_id, quantity, locations(name, code), lots(lot_number)')
      .eq('item_id', itemId),
    supabase
      .from('po_lines' as never)
      .select('quantity, received_quantity, conversion_to_base, purchase_orders!inner(id, po_number, status)')
      .eq('item_id', itemId),
  ]);
  if (levelsRes.error) throw levelsRes.error;
  if (poRes.error) throw poRes.error;

  const balances = (
    (levelsRes.data ?? []) as unknown as Array<{
      id: string;
      location_id: string;
      lot_id: string | null;
      quantity: number | string;
      locations: { name: string; code: string } | { name: string; code: string }[] | null;
      lots: { lot_number: string } | { lot_number: string }[] | null;
    }>
  )
    .map((row) => {
      const location = embeddedOne(row.locations);
      const lot = embeddedOne(row.lots);
      const quantity = Number(row.quantity);
      return {
        id: row.id,
        locationId: row.location_id,
        locationName: location?.name?.trim() || 'Unknown location',
        lotId: row.lot_id,
        lotNumber: lot?.lot_number ?? null,
        quantity,
      };
    })
    .filter((row) => Number.isFinite(row.quantity) && row.quantity !== 0);

  const byPo = new Map<string, OpenPurchaseOrderHold>();
  for (const row of (poRes.data ?? []) as unknown as Array<{
    quantity: number | string;
    received_quantity: number | string;
    conversion_to_base: number | string | null;
    purchase_orders:
      | { id: string; po_number: string; status: string }
      | Array<{ id: string; po_number: string; status: string }>
      | null;
  }>) {
    const po = embeddedOne(row.purchase_orders);
    if (!po || CLOSED_PO_STATUSES.has(po.status)) continue;
    const outstanding = Math.round(
      (Number(row.quantity) - Number(row.received_quantity)) * (Number(row.conversion_to_base) || 1),
    );
    if (!Number.isFinite(outstanding) || outstanding <= 0) continue;
    const existing = byPo.get(po.id);
    if (existing) existing.outstanding += outstanding;
    else byPo.set(po.id, { poNumber: po.po_number, status: po.status, outstanding });
  }

  return { balances, openPurchaseOrders: [...byPo.values()] };
}

/**
 * Zero every outstanding bucket for an item. Positive quantity is issued
 * as scrap. Negative quantity is adjusted up to zero. Callers archive
 * only after this returns, so the status guard never sees leftover stock.
 */
export async function scrapOutstandingInventory(
  organizationId: string,
  itemId: string,
  sku: string,
): Promise<{ scrapped: number; corrected: number }> {
  const readiness = await fetchArchiveReadiness(itemId);
  let scrapped = 0;
  let corrected = 0;

  for (const balance of readiness.balances) {
    const qty = Math.round(balance.quantity);
    if (qty === 0) continue;
    if (balance.lotId && !balance.lotNumber) {
      throw new Error('Could not read the lot on this stock. Refresh and try again.');
    }
    if (qty > 0) {
      await postStockMovement(organizationId, {
        itemId,
        type: 'issue',
        quantity: qty,
        fromLocationId: balance.locationId,
        lotNumber: balance.lotNumber,
        reasonCode: 'scrap',
        memo: `Scrapped remaining stock to archive ${sku}`,
        referenceType: 'item_archive',
        referenceId: itemId,
      });
      scrapped += qty;
    } else {
      await postStockMovement(organizationId, {
        itemId,
        type: 'adjustment',
        quantity: Math.abs(qty),
        toLocationId: balance.locationId,
        lotNumber: balance.lotNumber,
        reasonCode: 'count_correction',
        memo: `Cleared a negative balance to archive ${sku}`,
        referenceType: 'item_archive',
        referenceId: itemId,
      });
      corrected += Math.abs(qty);
    }
  }

  const left = await fetchArchiveReadiness(itemId);
  if (left.balances.length > 0) {
    throw new Error('Some inventory is still on hand.');
  }
  return { scrapped, corrected };
}

function invalidateInventory(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['stock-movements'] });
  qc.invalidateQueries({ queryKey: ['inventory-levels'] });
  qc.invalidateQueries({ queryKey: ['lots'] });
}

/** Scrap leftover on-hand without changing status. Used for items already archived. */
export function useScrapItemInventory() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { itemId: string; sku: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      return scrapOutstandingInventory(profile.organization_id, input.itemId, input.sku);
    },
    onSettled: () => invalidateInventory(qc),
  });
}

/**
 * Clear on-hand, then set status to archived. Stock movements are posted
 * first so the database guard, which rejects an archive while any bucket
 * is non-zero, sees an empty item.
 */
export function useArchiveItem() {
  const scrap = useScrapItemInventory();
  const updateItem = useUpdateItem();
  return useMutation({
    mutationFn: async (input: { itemId: string; sku: string }) => {
      const result = await scrap.mutateAsync(input);
      try {
        await updateItem.mutateAsync({ id: input.itemId, patch: { status: 'archived' } });
      } catch (err) {
        throw new Error(
          formatError(err, 'Stock was cleared, but the item could not be archived.'),
        );
      }
      return result;
    },
  });
}

export interface RecordAdjustmentInput {
  itemId: string;
  locationId: string;
  lotNumber?: string | null;
  /** The new on-hand count the operator entered. */
  countedQuantity: number;
  /** Current system on-hand at this location/lot, for delta computation. */
  currentQuantity: number;
  reasonCode: AdjustmentReason | string;
  memo?: string | null;
  /** 'cycle_count' for a physical count, 'adjustment' for a write-off etc. */
  type?: 'adjustment' | 'cycle_count';
}

/**
 * Reconcile system on-hand to a counted quantity by posting a single
 * adjustment / cycle-count movement for the delta. A positive delta
 * (counted > system) flows IN to the location; a negative delta flows
 * OUT. Refuses a zero delta so the ledger doesn't accumulate no-op rows.
 */
export function useRecordAdjustment() {
  const { profile } = useAuth();
  const record = useRecordStockMovement();
  return useMutation({
    mutationFn: async (input: RecordAdjustmentInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const counted = Math.round(input.countedQuantity);
      const current = Math.round(input.currentQuantity);
      if (!Number.isFinite(counted) || counted < 0) {
        throw new Error('Counted quantity must be zero or greater.');
      }
      const delta = counted - current;
      if (delta === 0) {
        throw new Error('The counted quantity matches the current on-hand — nothing to adjust.');
      }
      await record.mutateAsync({
        itemId: input.itemId,
        type: input.type ?? 'adjustment',
        quantity: Math.abs(delta),
        toLocationId: delta > 0 ? input.locationId : null,
        fromLocationId: delta < 0 ? input.locationId : null,
        lotNumber: input.lotNumber ?? null,
        reasonCode: input.reasonCode,
        memo: input.memo ?? null,
      });
      return { delta };
    },
  });
}

// -----------------------------------------------------------------------
// Item detail panels — suppliers, documents, BOM membership, sales,
// procurement, manufacturing.
//
// Each hook is intentionally small + scoped to a single panel so the
// detail page can request only what's mounted on the visible tab.
// -----------------------------------------------------------------------

export function useItemSuppliers(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-suppliers', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_suppliers' as never)
        .select(
          '*, vendors(id, name, email, website), item_supplier_price_breaks(id, min_qty, unit_cost_cents)',
        )
        .eq('item_id', itemId!)
        .order('is_primary', { ascending: false });
      if (error) throw error;
      return (data as unknown as ItemSupplier[]).map((row) => ({
        ...row,
        unit_cost_cents: row.unit_cost_cents == null ? null : Number(row.unit_cost_cents),
        purchase_uom_size: row.purchase_uom_size == null ? null : Number(row.purchase_uom_size),
        units_per_purchase_uom: Number(row.units_per_purchase_uom ?? 1) || 1,
        item_supplier_price_breaks: [...(row.item_supplier_price_breaks ?? [])]
          .map((b) => ({ ...b, min_qty: Number(b.min_qty), unit_cost_cents: Number(b.unit_cost_cents) }))
          .sort((a, b) => a.min_qty - b.min_qty),
      }));
    },
  });
}

/**
 * Input for attaching a vendor to an item as a supplier. Mirrors the
 * columns the Suppliers tab renders; `unitCostCents` is already in
 * cents, fractional cents allowed (the dialog converts the dollar input
 * via `inputDollarsToUnitCents`).
 */
export interface AddItemSupplierInput {
  itemId: string;
  vendorId: string;
  supplierPartNumber?: string | null;
  unitCostCents?: number | null;
  currency?: string;
  leadTimeDays?: number | null;
  minOrderQty?: number | null;
  orderMultiple?: number | null;
  purchaseUom?: string | null;
  purchaseUomSize?: number | null;
  purchaseUomSizeUom?: string | null;
  unitsPerPurchaseUom?: number | null;
  isPrimary?: boolean;
  notes?: string | null;
}

function purchaseUnitColumns(input: AddItemSupplierInput) {
  const uom = input.purchaseUom?.trim() || null;
  return {
    purchase_uom: uom,
    purchase_uom_size: uom ? (input.purchaseUomSize ?? null) : null,
    purchase_uom_size_uom: uom ? input.purchaseUomSizeUom?.trim() || null : null,
    units_per_purchase_uom: uom ? (input.unitsPerPurchaseUom ?? 1) : 1,
  };
}

/**
 * Attach a vendor to an item as an approved supplier from the
 * Suppliers tab. One row per (item, vendor) pair — the table's unique
 * constraint refuses a duplicate, which we translate into a friendly
 * message.
 *
 * `is_primary` is governed by the partial unique index
 * `uq_item_suppliers_primary` (at most one primary per item), so when
 * this row is flagged primary we demote any existing primary first to
 * avoid tripping the constraint.
 *
 * RLS (`item_suppliers_write_admin`) restricts writes to admin /
 * super_admin, so the call site gates on `canWrite(role, 'inventory')`.
 */
export function useAddItemSupplier() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: AddItemSupplierInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      if (!input.vendorId) throw new Error('Pick a vendor first.');

      // Demote the current primary before inserting a new primary so
      // the partial unique index doesn't reject the row.
      if (input.isPrimary) {
        const { error: demoteErr } = await supabase
          .from('item_suppliers' as never)
          .update({ is_primary: false } as never)
          .eq('item_id', input.itemId)
          .eq('is_primary', true);
        if (demoteErr) throw demoteErr;
      }

      const { data, error } = await supabase
        .from('item_suppliers' as never)
        .insert({
          organization_id: profile.organization_id,
          item_id: input.itemId,
          vendor_id: input.vendorId,
          supplier_part_number: input.supplierPartNumber?.trim() || null,
          unit_cost_cents: input.unitCostCents ?? null,
          currency: input.currency?.trim() || 'USD',
          lead_time_days: input.leadTimeDays ?? null,
          min_order_qty: input.minOrderQty ?? null,
          order_multiple: input.orderMultiple ?? null,
          ...purchaseUnitColumns(input),
          is_primary: input.isPrimary ?? false,
          notes: input.notes?.trim() || null,
        } as never)
        .select('*')
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error('This vendor is already a supplier for this item.');
        }
        throw error;
      }
      return data as unknown as ItemSupplier;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['item-suppliers', vars.itemId] });
      qc.invalidateQueries({ queryKey: ['item-cost-summaries'] });
      qc.invalidateQueries({ queryKey: ['bom-graph'] });
      qc.invalidateQueries({ queryKey: ITEM_PRIMARY_SUPPLIERS_KEY });
      qc.invalidateQueries({ queryKey: ['planning-suppliers'] });
      qc.invalidateQueries({ queryKey: ['vendor-item-terms'] });
      qc.invalidateQueries({ queryKey: ['supplier-part-numbers'] });
    },
  });
}

export interface UpdateItemSupplierInput extends AddItemSupplierInput {
  id: string;
}

/**
 * Correct an existing supplier row from the Suppliers tab. Same
 * primary-demotion and duplicate-vendor handling as
 * `useAddItemSupplier`; the demotion skips the row being edited so a
 * primary supplier can be re-saved without clearing its own flag.
 */
export function useUpdateItemSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateItemSupplierInput) => {
      if (!input.vendorId) throw new Error('Pick a vendor first.');

      if (input.isPrimary) {
        const { error: demoteErr } = await supabase
          .from('item_suppliers' as never)
          .update({ is_primary: false } as never)
          .eq('item_id', input.itemId)
          .eq('is_primary', true)
          .neq('id', input.id);
        if (demoteErr) throw demoteErr;
      }

      const { data, error } = await supabase
        .from('item_suppliers' as never)
        .update({
          vendor_id: input.vendorId,
          supplier_part_number: input.supplierPartNumber?.trim() || null,
          unit_cost_cents: input.unitCostCents ?? null,
          currency: input.currency?.trim() || 'USD',
          lead_time_days: input.leadTimeDays ?? null,
          min_order_qty: input.minOrderQty ?? null,
          order_multiple: input.orderMultiple ?? null,
          ...purchaseUnitColumns(input),
          is_primary: input.isPrimary ?? false,
          notes: input.notes?.trim() || null,
        } as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error('This vendor is already a supplier for this item.');
        }
        throw error;
      }
      return data as unknown as ItemSupplier;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['item-suppliers', vars.itemId] });
      qc.invalidateQueries({ queryKey: ['item-cost-summaries'] });
      qc.invalidateQueries({ queryKey: ['bom-graph'] });
      qc.invalidateQueries({ queryKey: ITEM_PRIMARY_SUPPLIERS_KEY });
      qc.invalidateQueries({ queryKey: ['planning-suppliers'] });
      qc.invalidateQueries({ queryKey: ['vendor-item-terms'] });
      qc.invalidateQueries({ queryKey: ['supplier-part-numbers'] });
    },
  });
}

export interface SaveItemSupplierPriceBreaksInput {
  itemId: string;
  itemSupplierId: string;
  breaks: Array<{ minQty: number; unitCostCents: number }>;
}

/**
 * Replace the quantity price breaks on one supplier row. The planner
 * reads these to show when ordering up to the next break costs less in
 * total (migration 150, `item_supplier_price_breaks`).
 */
export function useSaveItemSupplierPriceBreaks() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: SaveItemSupplierPriceBreaksInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const seen = new Set<number>();
      for (const brk of input.breaks) {
        if (!Number.isInteger(brk.minQty) || brk.minQty <= 0) {
          throw new Error('Each price break needs a whole-number quantity above zero.');
        }
        if (!Number.isFinite(brk.unitCostCents) || brk.unitCostCents < 0) {
          throw new Error('Each price break needs a unit price.');
        }
        if (seen.has(brk.minQty)) throw new Error(`Two price breaks start at ${brk.minQty}.`);
        seen.add(brk.minQty);
      }

      const { error: deleteErr } = await supabase
        .from('item_supplier_price_breaks' as never)
        .delete()
        .eq('item_supplier_id', input.itemSupplierId);
      if (deleteErr) throw deleteErr;

      if (input.breaks.length > 0) {
        const { error: insertErr } = await supabase.from('item_supplier_price_breaks' as never).insert(
          input.breaks.map((b) => ({
            organization_id: profile.organization_id,
            item_supplier_id: input.itemSupplierId,
            min_qty: b.minQty,
            unit_cost_cents: Math.round(b.unitCostCents),
          })) as never,
        );
        if (insertErr) throw insertErr;
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['item-suppliers', vars.itemId] });
      qc.invalidateQueries({ queryKey: ['item-cost-summaries'] });
      qc.invalidateQueries({ queryKey: ['bom-graph'] });
      qc.invalidateQueries({ queryKey: ITEM_PRIMARY_SUPPLIERS_KEY });
      qc.invalidateQueries({ queryKey: ['planning-suppliers'] });
      qc.invalidateQueries({ queryKey: ['vendor-item-terms'] });
      qc.invalidateQueries({ queryKey: ['supplier-part-numbers'] });
    },
  });
}

export function useItemDocuments(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-documents', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_documents' as never)
        .select('*')
        .eq('item_id', itemId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as unknown as ItemDocument[];
    },
  });
}

// -----------------------------------------------------------------------
// Item documents — file attachments (data sheets, CAD, photos, manuals)
//
// Bytes live in the private `item-documents` Storage bucket (migration
// 90) under `<organization_id>/<item_id>/<filename>`; the
// `item_documents` row stores the pointer + denormalised file info plus
// a `kind` label so the Documents tab can group / icon them. Mirrors the
// item-image uploader above and the project-attachment flow. Rows added
// by older flows may instead carry an external `url` and no
// storage_path — both are supported by the read/download UI.
// -----------------------------------------------------------------------

export type ItemDocumentKind = ItemDocument['kind'];

function buildItemDocumentPath(args: {
  organization_id: string;
  item_id: string;
  filename: string;
}): string {
  const stamp = Date.now();
  const cleaned = args.filename.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${args.organization_id}/${args.item_id}/${stamp}-${cleaned}`;
}

/**
 * Upload a document to the `item-documents` bucket and register it in
 * `item_documents`. Rolls back the storage object if the metadata insert
 * fails so we never strand orphaned bytes.
 */
export function useUploadItemDocument() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      itemId: string;
      file: File;
      kind: ItemDocumentKind;
      label?: string | null;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = buildItemDocumentPath({
        organization_id: profile.organization_id,
        item_id: input.itemId,
        filename: input.file.name,
      });

      const { error: uploadErr } = await supabase.storage
        .from(ITEM_DOCUMENTS_BUCKET)
        .upload(path, input.file, {
          upsert: false,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      const payload = {
        organization_id: profile.organization_id,
        item_id: input.itemId,
        kind: input.kind,
        label: input.label?.trim() || input.file.name,
        storage_bucket: ITEM_DOCUMENTS_BUCKET,
        storage_path: path,
        mime_type: input.file.type || null,
        size_bytes: input.file.size,
        uploaded_by: profile.id,
      };
      const { data, error: dbErr } = await supabase
        .from('item_documents' as never)
        .insert(payload as never)
        .select('*')
        .single();
      if (dbErr) {
        // Best-effort rollback so a failed insert doesn't leave the
        // uploaded object stranded in the bucket.
        await supabase.storage.from(ITEM_DOCUMENTS_BUCKET).remove([path]);
        throw dbErr;
      }
      return data as unknown as ItemDocument;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['item-documents', vars.itemId] });
    },
  });
}

/**
 * Delete a document. Removes the metadata row first (RLS short-circuits
 * if the caller lacks permission) then cleans up the storage object when
 * the row was a hosted file.
 */
export function useDeleteItemDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      itemId: string;
      storageBucket: string | null;
      storagePath: string | null;
    }) => {
      const { error: dbErr } = await supabase
        .from('item_documents' as never)
        .delete()
        .eq('id', input.id);
      if (dbErr) throw dbErr;
      if (input.storageBucket && input.storagePath) {
        await supabase.storage.from(input.storageBucket).remove([input.storagePath]);
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['item-documents', vars.itemId] });
    },
  });
}

/**
 * Short-lived signed URL for downloading / previewing a hosted item
 * document. Five-minute TTL — the bucket is private.
 */
export function useItemDocumentSignedUrl(
  bucket: string | null | undefined,
  path: string | null | undefined,
) {
  return useQuery({
    queryKey: ['item-document-url', bucket, path],
    enabled: !!bucket && !!path,
    staleTime: 4 * 60 * 1000,
    queryFn: async () => {
      if (!bucket || !path) return null;
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 60 * 5);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}

/**
 * BOMs whose ASSEMBLY is this item — i.e. "this item is built from these
 * components". Returns the active BOM (one per item by unique constraint)
 * with all of its components on the active version.
 */
export function useBomForItem(itemId: string | undefined) {
  return useQuery({
    queryKey: ['bom-for-item', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('boms' as never)
        .select(
          'id, name, description, is_active, bom_versions(id, version, is_active, bom_lines(id, quantity, uom, scrap_pct, items:component_item_id(id, sku, name, base_uom)))',
        )
        .eq('item_id', itemId!)
        .eq('is_active', true)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as {
        id: string;
        name: string;
        description: string | null;
        is_active: boolean;
        bom_versions: Array<{
          id: string;
          version: number;
          is_active: boolean;
          bom_lines: Array<{
            id: string;
            quantity: number;
            uom: string;
            scrap_pct: number;
            items: { id: string; sku: string; name: string; base_uom: string } | null;
          }>;
        }>;
      } | null;
    },
  });
}

/**
 * BOMs that CONSUME this item — i.e. "where used". Walks bom_lines for
 * any reference to the item and returns the parent BOM (and its
 * assembly item) so the operator can jump up the tree.
 */
export function useBomsContainingItem(itemId: string | undefined) {
  return useQuery({
    queryKey: ['boms-containing-item', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bom_lines' as never)
        .select(
          'id, quantity, uom, bom_versions!inner(id, version, is_active, boms!inner(id, name, is_active, items:item_id(id, sku, name)))',
        )
        .eq('component_item_id', itemId!);
      if (error) throw error;
      return data as unknown as Array<{
        id: string;
        quantity: number;
        uom: string;
        bom_versions: {
          id: string;
          version: number;
          is_active: boolean;
          boms: {
            id: string;
            name: string;
            is_active: boolean;
            items: { id: string; sku: string; name: string } | null;
          } | null;
        } | null;
      }>;
    },
  });
}

/**
 * Input for adding the current item onto a parent assembly's BOM as a
 * component. `parentItemId` is the assembly that should consume the
 * item; `componentItemId` is the item being added (the one whose
 * detail page the operator is on).
 */
export interface AddItemToBomInput {
  parentItemId: string;
  /** Used only when a brand-new BOM has to be created for the parent. */
  parentName: string;
  componentItemId: string;
  quantity: number;
  uom: string;
  scrapPct?: number;
}

/**
 * Add the current item as a component on a parent assembly's BOM
 * directly from the item's BOMs tab.
 *
 * The schema models a BOM as `boms` (one per assembly item) →
 * `bom_versions` (versioned recipe) → `bom_lines` (the components), so
 * adding a single line is a find-or-create walk down that tree:
 *
 *   1. Look up the parent assembly's BOM. Create the `boms` row if the
 *      assembly doesn't have one yet (unique on (org, item_id)).
 *   2. Resolve the target version — the active one, else the highest
 *      existing version, else a fresh v1 marked active.
 *   3. Refuse a duplicate component on that version (the DB has no
 *      unique constraint on (version, component) so we guard here).
 *   4. Insert the `bom_lines` row.
 *
 * RLS (`boms_write_admin` / `bom_versions_write` / `bom_lines_write`)
 * restricts every write to admin / super_admin, so the call site gates
 * the trigger on `canWrite(role, 'inventory')` to avoid a confusing
 * permission-denied round-trip for managers + employees.
 */
export function useAddItemToBom() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: AddItemToBomInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      if (input.parentItemId === input.componentItemId) {
        throw new Error('An item can’t be a component of itself.');
      }
      if (!Number.isFinite(input.quantity) || input.quantity <= 0) {
        throw new Error('Quantity must be greater than zero.');
      }
      const uom = input.uom.trim() || 'each';

      // 1. Find (or create) the parent assembly's BOM.
      const { data: existingBom, error: bomErr } = await supabase
        .from('boms' as never)
        .select('id, bom_versions(id, version, is_active)')
        .eq('item_id', input.parentItemId)
        .maybeSingle();
      if (bomErr) throw bomErr;

      let bomId: string;
      let versionId: string | null = null;

      if (existingBom) {
        const bom = existingBom as unknown as {
          id: string;
          bom_versions: Array<{ id: string; version: number; is_active: boolean }>;
        };
        bomId = bom.id;
        const versions = bom.bom_versions ?? [];
        const active = versions.find((v) => v.is_active);
        if (active) {
          versionId = active.id;
        } else if (versions.length > 0) {
          versionId = [...versions].sort((a, b) => b.version - a.version)[0].id;
        }
      } else {
        const { data: created, error } = await supabase
          .from('boms' as never)
          .insert({
            organization_id: profile.organization_id,
            item_id: input.parentItemId,
            name: input.parentName.trim() || 'Bill of materials',
            is_active: true,
          } as never)
          .select('id')
          .single();
        if (error) throw error;
        bomId = (created as unknown as { id: string }).id;
      }

      // 2. Ensure there's a version to hang the line on. A BOM with no
      //    versions (or none active) gets a fresh v1 marked active so
      //    MRP + the "built from" panel pick it up.
      if (!versionId) {
        const { data: createdVer, error } = await supabase
          .from('bom_versions' as never)
          .insert({
            bom_id: bomId,
            version: 1,
            is_active: true,
          } as never)
          .select('id')
          .single();
        if (error) throw error;
        versionId = (createdVer as unknown as { id: string }).id;
      }

      // 3. Guard against a duplicate component on the same version.
      const { data: siblings, error: dupErr } = await supabase
        .from('bom_lines' as never)
        .select('component_item_id, position')
        .eq('bom_version_id', versionId);
      if (dupErr) throw dupErr;
      const siblingRows = (siblings ?? []) as unknown as Array<{
        component_item_id: string;
        position: number | null;
      }>;
      if (siblingRows.some((s) => s.component_item_id === input.componentItemId)) {
        throw new Error('This item is already a component of that assembly.');
      }

      // 4. Insert the line at the end of the BOM.
      const { data, error } = await supabase
        .from('bom_lines' as never)
        .insert({
          bom_version_id: versionId,
          component_item_id: input.componentItemId,
          quantity: input.quantity,
          uom,
          scrap_pct: input.scrapPct ?? 0,
          position: siblingRows.reduce((max, s) => Math.max(max, s.position ?? 0), 0) + 1,
        } as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as { id: string };
    },
    onSuccess: (_data, vars) => {
      // The component's "where used" list and the parent's "built from"
      // list both change, so bust each.
      qc.invalidateQueries({ queryKey: ['boms-containing-item', vars.componentItemId] });
      qc.invalidateQueries({ queryKey: ['bom-for-item', vars.parentItemId] });
      qc.invalidateQueries({ queryKey: ['bom-graph'] });
    },
  });
}

/**
 * Open + recent sales-order lines for this item. We don't filter by status
 * here — the caller can split into "open" vs "closed" buckets so the
 * Sales tab can show both at once.
 */
export function useItemSalesOrderLines(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-so-lines', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('so_lines' as never)
        .select(
          'id, quantity, shipped_quantity, unit_price_cents, line_total_cents, sales_orders!inner(id, so_number, status, ordered_at, customer_email, customers:customer_id(id, name))',
        )
        .eq('item_id', itemId!)
        .order('id', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data as unknown as Array<{
        id: string;
        quantity: number;
        shipped_quantity: number;
        unit_price_cents: number;
        line_total_cents: number;
        sales_orders: {
          id: string;
          so_number: string;
          status: string;
          ordered_at: string;
          customer_email: string | null;
          customers: { id: string; name: string } | null;
        } | null;
      }>;
    },
  });
}

export function useItemPurchaseOrderLines(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-po-lines', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('po_lines' as never)
        .select(
          'id, uom, conversion_to_base, quantity, received_quantity, unit_price_cents, line_total_cents, purchase_orders!inner(id, po_number, status, ordered_at, expected_at, carrier, tracking_number, tracking_url, tracking_status, tracking_estimated_delivery, shipped_at, vendors:vendor_id(id, name), po_shipments!po_shipments_purchase_order_id_fkey(id, carrier, tracking_number, description, tracking_url, tracking_status, tracking_estimated_delivery, position))',
        )
        .eq('item_id', itemId!)
        .order('id', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data as unknown as Array<{
        id: string;
        /** Ordered unit; quantities and price are in it. */
        uom: string;
        /** Stock units per `uom`. */
        conversion_to_base: number;
        quantity: number;
        received_quantity: number;
        unit_price_cents: number;
        line_total_cents: number;
        purchase_orders: {
          id: string;
          po_number: string;
          status: string;
          ordered_at: string | null;
          expected_at: string | null;
          carrier: string | null;
          tracking_number: string | null;
          tracking_url: string | null;
          tracking_status: TrackingStatus | null;
          tracking_estimated_delivery: string | null;
          shipped_at: string | null;
          vendors: { id: string; name: string } | null;
          /** Every parcel on the PO (migration 157); the header tracking_* is a roll-up. */
          po_shipments: Array<{
            id: string;
            carrier: string | null;
            tracking_number: string;
            description: string | null;
            tracking_url: string | null;
            tracking_status: TrackingStatus | null;
            tracking_estimated_delivery: string | null;
            position: number;
          }>;
        } | null;
      }>;
    },
  });
}

// Placed with the vendor and not yet fully received. `internal_po` is an
// unplaced draft, so it isn't counted as on order.
const ON_ORDER_PO_STATUSES = ['ordered', 'in_production', 'shipped', 'partially_received'];

// Confirmed and not yet finished. A `draft` production order is awaiting
// review, so like an `internal_po` it isn't counted as on order.
export const ON_ORDER_PRODUCTION_STATUSES = ['planned', 'released', 'in_progress', 'blocked'];

export interface ItemOnOrder {
  quantity: number;
  /** Earliest expected arrival (YYYY-MM-DD) across the open lines, if any PO has one. */
  nextArrival: string | null;
}

/**
 * Outstanding quantity per item for the catalog grid: open purchase-order
 * lines plus open production orders for parts made in-house. PO arrival
 * uses the expected date, falling back to the carrier's estimated
 * delivery; a production order arrives on its planned end.
 */
export function useOnOrderByItem() {
  return useQuery({
    queryKey: ['item-po-lines', 'on-order'],
    queryFn: async () => {
      const [poResult, productionResult] = await Promise.all([
        supabase
          .from('po_lines' as never)
          .select(
            'item_id, quantity, received_quantity, conversion_to_base, purchase_orders!inner(status, expected_at, tracking_estimated_delivery)',
          )
          .not('item_id', 'is', null)
          .in('purchase_orders.status', ON_ORDER_PO_STATUSES),
        supabase
          .from('production_schedules' as never)
          .select('item_id, quantity, actual_quantity, planned_end')
          .not('item_id', 'is', null)
          .in('status', ON_ORDER_PRODUCTION_STATUSES),
      ]);
      if (poResult.error) throw poResult.error;
      if (productionResult.error) throw productionResult.error;
      const { data } = poResult;

      const byItem = new Map<string, ItemOnOrder>();
      const add = (itemId: string, outstanding: number, arrival: string | null) => {
        if (!Number.isFinite(outstanding) || outstanding <= 0) return;
        const existing = byItem.get(itemId);
        if (existing) {
          existing.quantity += outstanding;
          if (arrival && (!existing.nextArrival || arrival < existing.nextArrival)) {
            existing.nextArrival = arrival;
          }
        } else {
          byItem.set(itemId, { quantity: outstanding, nextArrival: arrival });
        }
      };

      for (const row of (productionResult.data ?? []) as unknown as Array<{
        item_id: string;
        quantity: number | string;
        actual_quantity: number | string | null;
        planned_end: string;
      }>) {
        add(
          row.item_id,
          Number(row.quantity) - Number(row.actual_quantity ?? 0),
          localIsoDate(row.planned_end),
        );
      }

      for (const row of (data ?? []) as unknown as Array<{
        item_id: string;
        quantity: number | string;
        received_quantity: number | string | null;
        conversion_to_base: number | string | null;
        purchase_orders:
          | { status: string; expected_at: string | null; tracking_estimated_delivery: string | null }
          | Array<{ status: string; expected_at: string | null; tracking_estimated_delivery: string | null }>
          | null;
      }>) {
        const po = embeddedOne(row.purchase_orders);
        if (!po) continue;
        add(
          row.item_id,
          Math.round(
            (Number(row.quantity) - Number(row.received_quantity ?? 0)) *
              (Number(row.conversion_to_base) || 1),
          ),
          po.expected_at ?? po.tracking_estimated_delivery?.slice(0, 10) ?? null,
        );
      }
      return byItem;
    },
  });
}

/**
 * Shares the `item-suppliers` prefix so a broad invalidation of the
 * per-item supplier lists refreshes this rollup too.
 */
export const ITEM_PRIMARY_SUPPLIERS_KEY = ['item-suppliers', 'primary-by-item'] as const;

/**
 * The supplier the catalog grid shows for each item, keyed by item id.
 * One round-trip over every `item_suppliers` row in the org; see
 * `rollupPrimarySuppliers` for how the primary is chosen.
 */
export function usePrimarySupplierByItem() {
  return useQuery({
    queryKey: ITEM_PRIMARY_SUPPLIERS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_suppliers' as never)
        .select(
          'item_id, vendor_id, supplier_part_number, is_primary, vendors:vendor_id(id, name)',
        );
      if (error) throw error;
      const rows = (
        (data ?? []) as unknown as Array<{
          item_id: string;
          vendor_id: string;
          supplier_part_number: string | null;
          is_primary: boolean;
          vendors: { id: string; name: string } | Array<{ id: string; name: string }> | null;
        }>
      ).map((row) => ({
        item_id: row.item_id,
        vendor_id: row.vendor_id,
        vendor_name: embeddedOne(row.vendors)?.name ?? null,
        supplier_part_number: row.supplier_part_number,
        is_primary: row.is_primary,
      }));
      return rollupPrimarySuppliers(rows);
    },
  });
}

/**
 * Each item's UoM Stocked (the unit it is bought and counted in, e.g.
 * roll = 1,800 in), keyed by item id. Only items whose purchase unit is a
 * real pack of the UoM Produced appear.
 */
export function useStockedUnitByItem() {
  return useQuery({
    queryKey: ['item-suppliers', 'stocked-unit-by-item'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_suppliers' as never)
        .select('item_id, is_primary, purchase_uom, purchase_uom_size, purchase_uom_size_uom, units_per_purchase_uom');
      if (error) throw error;
      type Row = {
        item_id: string;
        is_primary: boolean;
        purchase_uom: string | null;
        purchase_uom_size: number | null;
        purchase_uom_size_uom: string | null;
        units_per_purchase_uom: number | null;
      };
      const byItem = new Map<string, Row[]>();
      for (const row of (data ?? []) as unknown as Row[]) {
        const list = byItem.get(row.item_id) ?? [];
        list.push(row);
        byItem.set(row.item_id, list);
      }
      const out = new Map<string, PurchaseUnit>();
      for (const [itemId, rows] of byItem) {
        const unit = mainPurchaseUnit(rows);
        if (unit) out.set(itemId, unit);
      }
      return out;
    },
  });
}

export interface ItemProductionOrder {
  id: string;
  title: string;
  status: string;
  source: 'manual' | 'reorder_point';
  quantity: number;
  actual_quantity: number | null;
  planned_start: string;
  planned_end: string;
  work_center: { id: string; code: string; name: string } | null;
}

/** Production orders that make this item and aren't finished or cancelled, drafts included. */
export function useItemProductionOrders(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-production-orders', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('production_schedules' as never)
        .select(
          'id, title, status, source, quantity, actual_quantity, planned_start, planned_end, work_center:work_centers(id, code, name)',
        )
        .eq('item_id', itemId!)
        .not('status', 'in', '(completed,cancelled)')
        .order('planned_end');
      if (error) throw error;
      return ((data ?? []) as unknown as ItemProductionOrder[]).map((row) => ({
        ...row,
        quantity: Number(row.quantity),
        actual_quantity: row.actual_quantity == null ? null : Number(row.actual_quantity),
        work_center: embeddedOne(row.work_center),
      }));
    },
  });
}

/**
 * Work orders that PRODUCE this item (item_id on work_orders is the output).
 */
export function useItemProducingWorkOrders(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-wo-producing', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('work_orders' as never)
        .select(
          'id, wo_number, status, quantity, scheduled_start, scheduled_end, actual_start, actual_end',
        )
        .eq('item_id', itemId!)
        .order('scheduled_start', { ascending: false, nullsFirst: false })
        .limit(100);
      if (error) throw error;
      return data as unknown as Array<{
        id: string;
        wo_number: string;
        status: string;
        quantity: number;
        scheduled_start: string | null;
        scheduled_end: string | null;
        actual_start: string | null;
        actual_end: string | null;
      }>;
    },
  });
}

/**
 * Work orders that CONSUME this item — pulled from `wo_consumption` for
 * recorded usage, plus `bom_lines` for "would consume" that aren't yet
 * issued. We surface only the recorded-consumption side here; the BOM
 * predictions live in `useBomsContainingItem` already.
 */
export function useItemConsumingWorkOrders(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-wo-consuming', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wo_consumption' as never)
        .select(
          'id, quantity, recorded_at, work_orders!inner(id, wo_number, status, item_id, items:item_id(id, sku, name))',
        )
        .eq('component_item_id', itemId!)
        .order('recorded_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as unknown as Array<{
        id: string;
        quantity: number;
        recorded_at: string;
        work_orders: {
          id: string;
          wo_number: string;
          status: string;
          item_id: string;
          items: { id: string; sku: string; name: string } | null;
        } | null;
      }>;
    },
  });
}

// -----------------------------------------------------------------------
// Item images
//
// A gallery of photos per SKU (migration 158), stored in the private
// `item-images` Supabase Storage bucket and registered in
// `public.item_images`. `items.image_path` points at the primary photo,
// which is what list thumbnails and other surfaces render. Object keys
// are always
//   `<organization_id>/<item_id>/<filename>`
// so the storage RLS policy in migration 37 can decode the org segment
// and refuse cross-org access.
// -----------------------------------------------------------------------

export interface ItemImage {
  id: string;
  organization_id: string;
  item_id: string;
  storage_path: string;
  caption: string | null;
  position: number;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export const MAX_ITEM_IMAGE_BYTES = 10 * 1024 * 1024;

/**
 * Order a gallery for display: primary photo first, then by position
 * and upload time.
 */
export function sortItemImages(images: ItemImage[], primaryPath: string | null): ItemImage[] {
  return [...images].sort((a, b) => {
    const ap = a.storage_path === primaryPath ? 0 : 1;
    const bp = b.storage_path === primaryPath ? 0 : 1;
    if (ap !== bp) return ap - bp;
    if (a.position !== b.position) return a.position - b.position;
    return a.created_at.localeCompare(b.created_at);
  });
}

function sanitizeImageFilename(name: string): string {
  // Prepend a timestamp so re-uploads don't collide and the most recent
  // file sorts last. Strip everything that isn't a safe S3-style path
  // character so the storage key stays clean.
  const stamp = Date.now();
  const cleaned = name.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${stamp}-${cleaned}`;
}

export function buildItemImagePath(args: {
  organization_id: string;
  item_id: string;
  filename: string;
}): string {
  return `${args.organization_id}/${args.item_id}/${sanitizeImageFilename(args.filename)}`;
}

async function setItemPrimaryImage(itemId: string, path: string | null) {
  const { error } = await supabase
    .from('items' as never)
    .update({
      image_path: path,
      image_uploaded_at: path ? new Date().toISOString() : null,
    } as never)
    .eq('id', itemId);
  if (error) throw error;
}

function invalidateItemImages(qc: ReturnType<typeof useQueryClient>, itemId: string) {
  qc.invalidateQueries({ queryKey: ['item-images', itemId] });
  qc.invalidateQueries({ queryKey: ['item', itemId] });
  qc.invalidateQueries({ queryKey: ['items'] });
}

/** Every photo registered for an item, in gallery order (unsorted by primary). */
export function useItemImages(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-images', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_images' as never)
        .select('*')
        .eq('item_id', itemId!)
        .order('position', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as unknown as ItemImage[];
    },
  });
}

/**
 * Upload one photo into an item's gallery. Pass `makePrimary` to also
 * point `items.image_path` at it (the first photo on an item should).
 * Removes the stored object again if the gallery insert fails.
 */
export function useUploadItemImage() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      itemId: string;
      file: File;
      position: number;
      makePrimary?: boolean;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = buildItemImagePath({
        organization_id: profile.organization_id,
        item_id: input.itemId,
        filename: input.file.name,
      });

      const { error: uploadErr } = await supabase.storage
        .from(ITEM_IMAGES_BUCKET)
        .upload(path, input.file, {
          upsert: false,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      const { data, error: dbErr } = await supabase
        .from('item_images' as never)
        .insert({
          organization_id: profile.organization_id,
          item_id: input.itemId,
          storage_path: path,
          position: input.position,
          mime_type: input.file.type || null,
          size_bytes: input.file.size,
          uploaded_by: profile.id,
        } as never)
        .select('*')
        .single();
      if (dbErr) {
        await supabase.storage.from(ITEM_IMAGES_BUCKET).remove([path]);
        throw dbErr;
      }

      if (input.makePrimary) await setItemPrimaryImage(input.itemId, path);
      return data as unknown as ItemImage;
    },
    onSuccess: (_data, variables) => invalidateItemImages(qc, variables.itemId),
  });
}

export function useSetPrimaryItemImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { itemId: string; path: string }) => {
      await setItemPrimaryImage(input.itemId, input.path);
    },
    onSuccess: (_data, variables) => invalidateItemImages(qc, variables.itemId),
  });
}

export function useUpdateItemImageCaption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; itemId: string; caption: string | null }) => {
      const caption = input.caption?.trim() || null;
      const { error } = await supabase
        .from('item_images' as never)
        .update({ caption } as never)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['item-images', variables.itemId] });
    },
  });
}

/**
 * Delete a photo from the gallery. When it was the primary photo, the
 * next photo in gallery order takes over (or the primary is cleared).
 */
export function useDeleteItemImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { image: ItemImage; primaryPath: string | null }) => {
      const { image } = input;
      const { error } = await supabase
        .from('item_images' as never)
        .delete()
        .eq('id', image.id);
      if (error) throw error;
      await supabase.storage.from(ITEM_IMAGES_BUCKET).remove([image.storage_path]);

      if (input.primaryPath === image.storage_path) {
        const { data: next } = await supabase
          .from('item_images' as never)
          .select('storage_path')
          .eq('item_id', image.item_id)
          .order('position', { ascending: true })
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        const nextPath = (next as unknown as { storage_path: string } | null)?.storage_path ?? null;
        await setItemPrimaryImage(image.item_id, nextPath);
      }
    },
    onSuccess: (_data, variables) => invalidateItemImages(qc, variables.image.item_id),
  });
}

/**
 * Mint a short-lived signed URL for an item image so the browser can
 * render it. Five minutes is plenty for a single page view, and the
 * bucket is private so we never want a long-lived link.
 */
export function useItemImageSignedUrl(itemId: string | undefined, path: string | null | undefined) {
  return useQuery({
    queryKey: ['item-image-url', itemId, path],
    enabled: !!itemId && !!path,
    staleTime: 4 * 60 * 1000,
    queryFn: async () => {
      if (!path) return null;
      const { data, error } = await supabase.storage
        .from(ITEM_IMAGES_BUCKET)
        .createSignedUrl(path, 60 * 5);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}

// -----------------------------------------------------------------------
// Item tags — colour-coded labels (migration 40)
//
// Two surfaces:
//   * `useItemTagCatalog()`     – every tag in the org, used to populate
//                                 the picker dropdown + autocomplete.
//   * `useTagsForItem(itemId)`  – the tags currently attached to one
//                                 item, rendered as badges in the
//                                 Item details card.
//
// Writes:
//   * `useCreateItemTag()`      – idempotent on (org, lower(name)).
//                                 Returns the existing row if a tag
//                                 with that name is already in the
//                                 catalog so the picker can "create or
//                                 attach" with a single click.
//   * `useUpdateItemTag()`      – rename / recolour an existing tag.
//   * `useDeleteItemTag()`      – remove a tag entirely (cascades the
//                                 assignment join rows).
//   * `useSetItemTags()`        – replace the tag set on an item with
//                                 a desired list, computing the diff
//                                 against the current cache so we only
//                                 INSERT/DELETE the differences.
// -----------------------------------------------------------------------

/**
 * Every tag in the caller's organisation, ordered by name. Used to
 * populate the picker dropdown and the autocomplete on the "create
 * tag" inline form.
 */
export function useItemTagCatalog() {
  return useQuery({
    queryKey: ['item-tag-catalog'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_tags' as never)
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      return data as unknown as ItemTag[];
    },
  });
}

/**
 * The tags currently attached to a single item, hydrated with the
 * tag's name + colour so the detail page can render badges without a
 * second round-trip. Sorted by tag name to match the catalog order.
 */
export function useTagsForItem(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-tags-for-item', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_tag_assignments' as never)
        .select(
          'tag_id, item_tags:tag_id(id, organization_id, name, color, created_at, updated_at)',
        )
        .eq('item_id', itemId!);
      if (error) throw error;
      const rows = data as unknown as Array<{
        tag_id: string;
        item_tags: ItemTag | null;
      }>;
      // Drop any orphaned rows where the join projection didn't return
      // a tag (e.g. a tag that was racing a delete) and surface the
      // tag rows directly so the caller doesn't have to unpack the
      // join shape on every render.
      const tags = rows.map((r) => r.item_tags).filter((t): t is ItemTag => t != null);
      return tags.sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

/**
 * Create a new tag in the org's catalog. Idempotent on the
 * case-insensitive unique index (`uq_item_tags_org_name`): if a tag
 * with the same name already exists we surface the existing row
 * instead of throwing, so the picker UI can call this from the
 * "Create new tag" inline form without first checking whether the
 * name is taken. The existing row's colour is preserved — re-creating
 * an existing tag never overwrites its swatch.
 */
export function useCreateItemTag() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { name: string; color: ItemTagColor }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const name = input.name.trim();
      if (!name) throw new Error('Tag name is required.');
      if (name.length > 64) throw new Error('Tag name must be 64 characters or fewer.');

      // Check for an existing tag (case-insensitive) before insert so
      // the duplicate path stays cheap and we don't burn an INSERT
      // round-trip every time the user clicks an existing name in the
      // search results.
      const existing = await supabase
        .from('item_tags' as never)
        .select('*')
        .eq('organization_id', profile.organization_id)
        .ilike('name', name)
        .maybeSingle();
      if (existing.error) throw existing.error;
      if (existing.data) {
        return existing.data as unknown as ItemTag;
      }

      const { data, error } = await supabase
        .from('item_tags' as never)
        .insert({
          organization_id: profile.organization_id,
          name,
          color: input.color,
        } as never)
        .select('*')
        .single();
      if (error) {
        // Race window: another tab inserted the same name between our
        // SELECT and INSERT. Fall back to the existing row so the
        // caller doesn't have to retry.
        if ((error as { code?: string }).code === '23505') {
          const retry = await supabase
            .from('item_tags' as never)
            .select('*')
            .eq('organization_id', profile.organization_id)
            .ilike('name', name)
            .maybeSingle();
          if (retry.data) return retry.data as unknown as ItemTag;
        }
        throw error;
      }
      return data as unknown as ItemTag;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-tag-catalog'] });
    },
  });
}

/** Rename or recolour an existing tag. Hits every item that uses it. */
export function useUpdateItemTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: { name?: string; color?: ItemTagColor } }) => {
      const cleaned: Record<string, unknown> = {};
      if (input.patch.name !== undefined) {
        const trimmed = input.patch.name.trim();
        if (!trimmed) throw new Error('Tag name is required.');
        if (trimmed.length > 64) {
          throw new Error('Tag name must be 64 characters or fewer.');
        }
        cleaned.name = trimmed;
      }
      if (input.patch.color !== undefined) {
        cleaned.color = input.patch.color;
      }
      if (Object.keys(cleaned).length === 0) {
        const { data, error } = await supabase
          .from('item_tags' as never)
          .select('*')
          .eq('id', input.id)
          .single();
        if (error) throw error;
        return data as unknown as ItemTag;
      }
      const { data, error } = await supabase
        .from('item_tags' as never)
        .update(cleaned as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error('A tag with that name already exists.');
        }
        throw error;
      }
      return data as unknown as ItemTag;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-tag-catalog'] });
      // The hydrated tags-for-item caches embed the tag row, so every
      // item the renamed/recoloured tag is attached to needs a refresh.
      qc.invalidateQueries({ queryKey: ['item-tags-for-item'] });
    },
  });
}

/**
 * Remove a tag from the catalog entirely. The DB cascades the
 * `item_tag_assignments` rows that referenced it, so any item that
 * was carrying this tag will lose it on its next fetch.
 */
export function useDeleteItemTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { error } = await supabase
        .from('item_tags' as never)
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-tag-catalog'] });
      qc.invalidateQueries({ queryKey: ['item-tags-for-item'] });
    },
  });
}

/**
 * Merge two tags together: every item carrying `sourceId` is
 * reassigned to `targetId`, then the source row is deleted from the
 * catalog. Surfaces the rename-collision UX in `TagsPicker` — when a
 * user fixes a misspelling or casing variant ("best seler" →
 * "Best Seller") and the corrected name already exists in the
 * catalog, we'd otherwise hit the case-insensitive unique index
 * (`uq_item_tags_org_name`) and refuse the rename. Merging is the
 * useful answer there: consolidate the two duplicate tags into one
 * canonical row and carry every item that referenced either side
 * over to the survivor.
 *
 * Implementation:
 *
 *   1. Look up which items currently carry the source tag. We could
 *      do an UPDATE … SET tag_id = target WHERE tag_id = source, but
 *      that would explode on the (item_id, tag_id) unique constraint
 *      whenever an item is already tagged with both — exactly the
 *      "this item has best seler AND Best Seller" duplicate the
 *      operator is trying to clean up. So instead we...
 *   2. Upsert a (target) assignment for each of those items, with
 *      onConflict ignoring rows that already exist. Items that had
 *      only the source get a target row added; items that already
 *      had both keep just the existing target.
 *   3. Delete the source tag. The FK cascade on
 *      `item_tag_assignments.tag_id` removes the leftover source
 *      assignments, leaving every affected item carrying exactly
 *      one survivor.
 *
 * Cache invalidation is broad — both the catalog and every
 * `item-tags-for-item` query — because this can touch any number of
 * item rows in one shot and we don't track which.
 */
export function useMergeItemTags() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { sourceId: string; targetId: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      if (input.sourceId === input.targetId) {
        throw new Error('Cannot merge a tag into itself.');
      }

      const { data: sourceAssignments, error: fetchErr } = await supabase
        .from('item_tag_assignments' as never)
        .select('item_id')
        .eq('tag_id', input.sourceId);
      if (fetchErr) throw fetchErr;

      const itemIds = Array.from(
        new Set(
          ((sourceAssignments ?? []) as Array<{ item_id: string }>).map((r) => r.item_id),
        ),
      );

      if (itemIds.length > 0) {
        const rows = itemIds.map((itemId) => ({
          organization_id: profile.organization_id,
          item_id: itemId,
          tag_id: input.targetId,
        }));
        const { error: upsertErr } = await supabase
          .from('item_tag_assignments' as never)
          .upsert(rows as never, {
            onConflict: 'item_id,tag_id',
            ignoreDuplicates: true,
          });
        if (upsertErr) throw upsertErr;
      }

      // Delete the source tag last — the FK cascade clears any
      // remaining source-side assignments. Doing this *after* the
      // upsert (rather than relying on a single UPDATE) keeps the
      // operation safe to retry: if the upsert fails we haven't
      // destroyed the source yet.
      const { error: deleteErr } = await supabase
        .from('item_tags' as never)
        .delete()
        .eq('id', input.sourceId);
      if (deleteErr) throw deleteErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-tag-catalog'] });
      qc.invalidateQueries({ queryKey: ['item-tags-for-item'] });
    },
  });
}

/**
 * Replace the tag set on an item with a desired list of tag ids.
 * Diffs against `currentTagIds` so we only INSERT the new ones and
 * DELETE the dropped ones, instead of clearing + re-inserting and
 * burning audit-log rows for unchanged links — same shape as
 * `useSetTaskAssignees` over in features/projects.
 */
export function useSetItemTags() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { itemId: string; tagIds: string[]; currentTagIds: string[] }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const desired = Array.from(new Set(input.tagIds.filter(Boolean)));
      const current = new Set(input.currentTagIds);
      const desiredSet = new Set(desired);
      const toAdd = desired.filter((id) => !current.has(id));
      const toRemove = input.currentTagIds.filter((id) => !desiredSet.has(id));

      const ops: Promise<void>[] = [];
      if (toAdd.length > 0) {
        const rows = toAdd.map((tagId) => ({
          organization_id: profile.organization_id,
          item_id: input.itemId,
          tag_id: tagId,
        }));
        ops.push(
          (async () => {
            const { error } = await supabase
              .from('item_tag_assignments' as never)
              .upsert(rows as never, { onConflict: 'item_id,tag_id' });
            if (error) throw error;
          })(),
        );
      }
      if (toRemove.length > 0) {
        ops.push(
          (async () => {
            const { error } = await supabase
              .from('item_tag_assignments' as never)
              .delete()
              .eq('item_id', input.itemId)
              .in('tag_id', toRemove);
            if (error) throw error;
          })(),
        );
      }
      await Promise.all(ops);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['item-tags-for-item', vars.itemId] });
      // The catalog query doesn't change here, but hold off on
      // invalidating it — assignments don't affect the tag rows.
    },
  });
}

// -----------------------------------------------------------------------
// Workstations — per-org managed vocabulary (migration 94)
//
// Mirrors the item_tags surfaces:
//   * useWorkstations()            – every workstation in the org (picker
//                                    dropdown + faceted filter source).
//   * useWorkstationsForItem(id)   – the stations attached to one item.
//   * useCreateWorkstation()       – idempotent on (org, lower(name)).
//   * useUpdateWorkstation()       – rename an existing station.
//   * useDeleteWorkstation()       – remove a station (cascades the join).
//   * useSetItemWorkstations()     – replace an item's station set via a
//                                    diff so we only INSERT/DELETE changes.
// -----------------------------------------------------------------------

/** Every workstation in the caller's org, ordered by position then name. */
export function useWorkstations() {
  return useQuery({
    queryKey: ['workstations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workstations' as never)
        .select('*')
        .order('position', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data as unknown as Workstation[];
    },
  });
}

/** The workstations currently attached to a single item. */
export function useWorkstationsForItem(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-workstations-for-item', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_workstations' as never)
        .select('workstation_id, workstations:workstation_id(id, organization_id, name, position, created_at, updated_at)')
        .eq('item_id', itemId!);
      if (error) throw error;
      const rows = data as unknown as Array<{
        workstation_id: string;
        workstations: Workstation | null;
      }>;
      const stations = rows
        .map((r) => r.workstations)
        .filter((w): w is Workstation => w != null);
      return stations.sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

/**
 * Create a workstation in the org's catalog. Idempotent on the
 * case-insensitive unique index: if a station with the same name
 * already exists we surface the existing row instead of throwing, so
 * the picker can "create or attach" with a single click.
 */
export function useCreateWorkstation() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { name: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const name = input.name.trim();
      if (!name) throw new Error('Workstation name is required.');
      if (name.length > 64) throw new Error('Workstation name must be 64 characters or fewer.');

      const existing = await supabase
        .from('workstations' as never)
        .select('*')
        .eq('organization_id', profile.organization_id)
        .ilike('name', name)
        .maybeSingle();
      if (existing.error) throw existing.error;
      if (existing.data) return existing.data as unknown as Workstation;

      const { data, error } = await supabase
        .from('workstations' as never)
        .insert({ organization_id: profile.organization_id, name } as never)
        .select('*')
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          const retry = await supabase
            .from('workstations' as never)
            .select('*')
            .eq('organization_id', profile.organization_id)
            .ilike('name', name)
            .maybeSingle();
          if (retry.data) return retry.data as unknown as Workstation;
        }
        throw error;
      }
      return data as unknown as Workstation;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workstations'] });
    },
  });
}

/** Rename an existing workstation. Propagates to every item that uses it. */
export function useUpdateWorkstation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: { name?: string; position?: number } }) => {
      const cleaned: Record<string, unknown> = {};
      if (input.patch.name !== undefined) {
        const trimmed = input.patch.name.trim();
        if (!trimmed) throw new Error('Workstation name is required.');
        if (trimmed.length > 64) throw new Error('Workstation name must be 64 characters or fewer.');
        cleaned.name = trimmed;
      }
      if (input.patch.position !== undefined) cleaned.position = input.patch.position;

      const { data, error } = await supabase
        .from('workstations' as never)
        .update(cleaned as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error('A workstation with that name already exists.');
        }
        throw error;
      }
      return data as unknown as Workstation;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workstations'] });
      qc.invalidateQueries({ queryKey: ['item-workstations-for-item'] });
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
}

/** Remove a workstation from the catalog. Cascades the join rows. */
export function useDeleteWorkstation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { error } = await supabase
        .from('workstations' as never)
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workstations'] });
      qc.invalidateQueries({ queryKey: ['item-workstations-for-item'] });
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
}

/**
 * Replace the workstation set on an item with a desired list of ids.
 * Diffs against `currentIds` so we only INSERT the new ones and DELETE
 * the dropped ones — same shape as `useSetItemTags`.
 */
export function useSetItemWorkstations() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      itemId: string;
      workstationIds: string[];
      currentIds: string[];
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const desired = Array.from(new Set(input.workstationIds.filter(Boolean)));
      const current = new Set(input.currentIds);
      const desiredSet = new Set(desired);
      const toAdd = desired.filter((id) => !current.has(id));
      const toRemove = input.currentIds.filter((id) => !desiredSet.has(id));

      const ops: Promise<void>[] = [];
      if (toAdd.length > 0) {
        const rows = toAdd.map((workstationId) => ({
          organization_id: profile.organization_id,
          item_id: input.itemId,
          workstation_id: workstationId,
        }));
        ops.push(
          (async () => {
            const { error } = await supabase
              .from('item_workstations' as never)
              .upsert(rows as never, { onConflict: 'item_id,workstation_id' });
            if (error) throw error;
          })(),
        );
      }
      if (toRemove.length > 0) {
        ops.push(
          (async () => {
            const { error } = await supabase
              .from('item_workstations' as never)
              .delete()
              .eq('item_id', input.itemId)
              .in('workstation_id', toRemove);
            if (error) throw error;
          })(),
        );
      }
      await Promise.all(ops);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['item-workstations-for-item', vars.itemId] });
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
}

// -----------------------------------------------------------------------
// Item kinds catalog (migration 41)
//
// Per-org taxonomy axis on items.kind. Read by anyone in the org so
// dropdowns / faceted filters / badges render; writes are super_admin
// only because adding a new kind affects every consumer of items
// (BOMs, MRP, the detail page's per-kind config, etc.).
//
// The three baseline kinds (raw_stock, raw_material, finished_good)
// are seeded as is_system rows for every org (migration 91 trimmed the
// catalog down from six). Triggers in migration 42 refuse deletes /
// slug changes on system rows; the UI hides the destructive
// affordances on top of that.
// -----------------------------------------------------------------------

/**
 * Every kind in the caller's organisation, ordered by `position` then
 * label so the baseline four sit at the top and custom kinds append
 * underneath. The list is the source of truth for dropdowns, faceted
 * filters, and badge styling on the items grid + detail page.
 */
export function useItemKinds() {
  return useQuery({
    queryKey: ['item-kinds'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_kinds' as never)
        .select('*')
        .order('position', { ascending: true })
        .order('label', { ascending: true });
      if (error) throw error;
      return data as unknown as ItemKindRow[];
    },
  });
}

/**
 * Mint a new kind in the caller's org. Slug defaults to a
 * snake_cased projection of the label so callers can pass the label
 * alone; pass `slug` explicitly to override (e.g. when reviving a
 * legacy slug). RLS gates this to super_admin in the DB; the UI hides
 * the affordance for everyone else so the call never round-trips with
 * a permission denied for a non-super-admin who somehow got here.
 */
export function useCreateItemKind() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      label: string;
      slug?: string;
      description?: string | null;
      color?: ItemKindColor;
      position?: number;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const label = input.label.trim();
      if (!label) throw new Error('Kind label is required.');
      if (label.length > 64) throw new Error('Kind label must be 64 characters or fewer.');

      const slug = (input.slug ?? slugify(label)).trim();
      if (!slug) throw new Error('Kind slug is required.');
      if (slug.length > 64) throw new Error('Kind slug must be 64 characters or fewer.');

      const payload: Record<string, unknown> = {
        organization_id: profile.organization_id,
        slug,
        label,
        description: input.description?.trim() || null,
        color: input.color ?? 'slate',
        is_system: false,
        position: input.position ?? 100,
      };

      const { data, error } = await supabase
        .from('item_kinds' as never)
        .insert(payload as never)
        .select('*')
        .single();
      if (error) {
        // 23505 covers both the (org, slug) unique constraint and the
        // case-insensitive (org, lower(label)) unique index. We don't
        // know which one tripped, but either way the operator just
        // needs a different name.
        if ((error as { code?: string }).code === '23505') {
          throw new Error(`A kind named “${label}” already exists.`);
        }
        throw error;
      }
      return data as unknown as ItemKindRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-kinds'] });
    },
  });
}

/**
 * Rename / recolour / reposition an existing kind. The DB-side
 * `tg_item_kinds_protect_system` trigger refuses slug changes on
 * system rows, so the UI omits that field on those rows.
 */
export function useUpdateItemKind() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: {
        label?: string;
        description?: string | null;
        color?: ItemKindColor;
        position?: number;
      };
    }) => {
      const cleaned: Record<string, unknown> = {};
      if (input.patch.label !== undefined) {
        const trimmed = input.patch.label.trim();
        if (!trimmed) throw new Error('Kind label is required.');
        if (trimmed.length > 64) {
          throw new Error('Kind label must be 64 characters or fewer.');
        }
        cleaned.label = trimmed;
      }
      if (input.patch.description !== undefined) {
        const raw = input.patch.description;
        cleaned.description = raw === null ? null : raw.trim() || null;
      }
      if (input.patch.color !== undefined) cleaned.color = input.patch.color;
      if (input.patch.position !== undefined) cleaned.position = input.patch.position;

      if (Object.keys(cleaned).length === 0) {
        const { data, error } = await supabase
          .from('item_kinds' as never)
          .select('*')
          .eq('id', input.id)
          .single();
        if (error) throw error;
        return data as unknown as ItemKindRow;
      }

      const { data, error } = await supabase
        .from('item_kinds' as never)
        .update(cleaned as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error('A kind with that name already exists.');
        }
        throw error;
      }
      return data as unknown as ItemKindRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-kinds'] });
      // Items embed kind via slug; only the badge rendering uses the
      // (label, color) lookup, but the catalog change should redraw
      // the grid + detail page on next render.
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['item'] });
    },
  });
}

/**
 * Delete a kind from the catalog. The DB blocks deletion when:
 *   * the row is a system kind (trigger guard);
 *   * any item still references the slug (FK on items.kind).
 *
 * We surface friendly errors for both so the operator knows which
 * rule they ran into.
 */
export function useDeleteItemKind() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { error } = await supabase
        .from('item_kinds' as never)
        .delete()
        .eq('id', input.id);
      if (error) {
        const code = (error as { code?: string }).code;
        if (code === '23503') {
          throw new Error(
            'This kind is still used by one or more items. Move those items to another kind first.',
          );
        }
        if (code === '2BP01' || /system/i.test(error.message ?? '')) {
          throw new Error('Built-in kinds can be renamed or recoloured but not deleted.');
        }
        throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-kinds'] });
    },
  });
}

/**
 * Best-effort label → slug projection: lowercase, ASCII alnum only,
 * collapsed to underscores, trimmed of leading/trailing punctuation,
 * capped to 64 chars. Falls back to a timestamp if the result is
 * empty (e.g. a label of all punctuation) so we never insert an empty
 * slug.
 */
function slugify(label: string): string {
  const cleaned = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
  return cleaned || `kind_${Date.now()}`;
}
